import * as Form from "@radix-ui/react-form";
import React, { useEffect, useState, lazy, Suspense, useRef } from "react";
import { Flex, Separator, Text, Button, Link, Tooltip, TextField, Tabs, CheckboxGroup, TextArea, Checkbox, IconButton, RadioGroup, HoverCard, Box, Badge, Progress, Switch, AlertDialog, Select } from "@radix-ui/themes";
import { UploadIcon, InfoCircledIcon, ExclamationTriangleIcon, PlusIcon, ArrowLeftIcon, DotsHorizontalIcon } from "@radix-ui/react-icons";
import { ArrowTopRightOnSquareIcon, ComputerDesktopIcon, DevicePhoneMobileIcon, SunIcon, MoonIcon } from "@heroicons/react/16/solid";
import { IPhoneMockup } from "react-device-mockup";
import { debounce } from "lodash";
import {
  useGetAllGuestPortalSettingsForPlan,
  useUpdateAllGuestPortalSettingsForPlan,
  useGetOrganisationDetails,
  useGetAirshipStatus,
  useGetMetaStatus,
  useLiveAccessPoints,
  useGetPlansGuestPortalSettings,
  useCreateGuestPortalPeriod,
  useDeleteGuestPortalPeriodByName,
  useUpdateGuestPortalPlanPeriods,
  useGetAtreemoStatus,
  useGetComoStatus,
  useGetDotdigitalStatus,
} from "hooks";
import { useToast } from "../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useParams, useLocation as useRouterLocation, useNavigate } from "react-router-dom";
import LinkEditor from "./LinkEditor";
import PeriodSelector from "./PeriodSelector";
import { DEFAULT_PORTAL_FONT, PORTAL_FONTS, getPortalFontCssFamily } from "./portalFonts";
import LogoSizeControl from "./LogoSizeControl";
import { DEFAULT_LOGO_SIZE, normalizeLogoSize } from "./portalLogoSize";

const CaptivePortalMockup = lazy(() => import("./CaptivePortalMockup"));
const EditPlanPopover = lazy(() => import("./EditPlanPopover"));

// Utility function to validate file size
const validateFileSize = (file) => {
  const maxSizeInBytes = 1048576; // 1MB
  if (file && file.size > maxSizeInBytes) {
    return false;
  }
  return true;
};

const DATA_FIELD_CHANGE_KEYS = [
  "email_flag",
  "age_flag",
  "gender_flag",
  "first_name_flag",
  "last_name_flag",
  "phone_number_flag",
  "dob_flag",
  "organisation_flag",
];

const didDataFieldsChange = (changesByPeriod) =>
  Object.values(changesByPeriod || {}).some((changes) =>
    DATA_FIELD_CHANGE_KEYS.some((key) => Object.prototype.hasOwnProperty.call(changes || {}, key))
  );

const GuestPortal = () => {
  const params = useParams();
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();

  // Track form state changes
  const [isDirty, setIsDirty] = useState(false);
  const [showIntegrationsReminder, setShowIntegrationsReminder] = useState(false);
  const [isInitializedForm, setIsInitializedForm] = useState(false);
  const prevIsInitializedRef = useRef(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  // Change tracking states
  const [periodChanges, setPeriodChanges] = useState({}); // Track changes by period ID
  const [newPeriods, setNewPeriods] = useState([]); // Track newly created periods

  // Determine if we're viewing a brand or location portal
  const isBrandRoute = routerLocation.pathname.includes("/portal-brand/");
  const isLocationRoute = routerLocation.pathname.includes("/portal-location/");

  // Get the ID from the URL parameters (either brandId or locationId)
  const paramId = isBrandRoute ? parseInt(params.brandId) : parseInt(params.locationId);

  // Get the brand info from router state
  const { isBrand, brandId, brandName, brandLocationCount, locationName, locationAddress, planId } = routerLocation.state || {};

  // Get location context for additional location data if needed
  const { locationsData } = useLocationContext();
  const { liveAccessPointsStatus } = useLiveAccessPoints(locationsData);
  const { userProfile } = useProfileContext();

  // State to store the effective locationId we'll use for fetching settings
  const [effectiveLocationId, setEffectiveLocationId] = useState(null);

  // Find a location ID for the brand route
  useEffect(() => {
    if (isBrandRoute && locationsData) {
      // Find the first location that belongs to this brand
      const locationForBrand = locationsData.find((location) => location.brand_id === paramId);
      if (locationForBrand) {
        setEffectiveLocationId(locationForBrand.id);
      } else {
        // If no location found for this brand then we use the brandId directly
        setEffectiveLocationId(paramId);
      }
    } else if (isLocationRoute) {
      // For location route, use the locationId directly
      setEffectiveLocationId(paramId);
    }
  }, [isBrandRoute, isLocationRoute, paramId, locationsData, navigate, routerLocation.state]);

  const { data: orgDetails, isLoading: isLoadingOrgDetails } = useGetOrganisationDetails(userProfile.organisation_id);
  const { data: airshipStatusData, isLoading: isStatusLoading } = useGetAirshipStatus(
    userProfile.organisation_id,
    isBrandRoute ? null : effectiveLocationId,
    isBrandRoute ? paramId : null
  );
  const isAirshipActive = () => {
    if (isStatusLoading) return false;
    return airshipStatusData?.data?.is_active === true;
  };

  const { data: metaStatusData, isLoading: isMetaStatusLoading } = useGetMetaStatus(
    userProfile.organisation_id,
    isBrandRoute ? null : effectiveLocationId,
    isBrandRoute ? paramId : null
  );
  const isMetaActive = () => {
    if (isMetaStatusLoading) return false;
    return metaStatusData?.data?.is_active === true;
  };

  const { data: atreemoStatusData, isLoading: isAtreemoStatusLoading } = useGetAtreemoStatus(
    userProfile.organisation_id,
    isBrandRoute ? null : effectiveLocationId,
    isBrandRoute ? paramId : null
  );
  const isAtreemoActive = () => {
    if (isAtreemoStatusLoading) return false;
    return atreemoStatusData?.data?.is_active === true;
  };

  const { data: comoStatusData, isLoading: isComoStatusLoading } = useGetComoStatus(
    userProfile.organisation_id,
    isBrandRoute ? null : effectiveLocationId,
    isBrandRoute ? paramId : null
  );
  const isComoActive = () => {
    if (isComoStatusLoading) return false;
    return comoStatusData?.data?.is_active === true;
  };

  const { data: dotdigitalStatusData, isLoading: isDotdigitalStatusLoading } = useGetDotdigitalStatus(userProfile.organisation_id);
  const isDotdigitalActive = () => {
    if (isDotdigitalStatusLoading) return false;
    return dotdigitalStatusData?.data?.is_active === true;
  };

  const isIntegrationActive = () => {
    return isAirshipActive() || isMetaActive() || isAtreemoActive() || isComoActive();
  };

  const { setToastMessage } = useToast();
  // const [view, setView] = useState("mobile");
  const [theme, setTheme] = useState("light");
  const [view, setView] = useState("desktop");
  const [screenWidthMobile, setScreenWidthMobile] = useState(250); // Default screenWidth for IPhoneMockup
  const [scaleFactorMobile, setScaleFactorMobile] = useState(1); // Default scale factor for IPhoneMockup
  const [isSaveChangesLoading, setIsSaveChangesLoading] = useState(false);
  const createGuestPortalPeriod = useCreateGuestPortalPeriod();
  const deleteGuestPortalPeriodByName = useDeleteGuestPortalPeriodByName();
  const updateGuestPortalPlanPeriods = useUpdateGuestPortalPlanPeriods();

  // Mobile view calculations for IPhoneMockup
  useEffect(() => {
    const handleResize = debounce(() => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      // Mobile view calculations
      const aspectRatio = 19.5 / 9;
      const maxDesiredWidth = 390; // The maximum desired screenWidth for IPhoneMockup
      const offset = 250;
      const availableHeight = windowHeight - offset;

      const calculatedScreenWidth = Math.min(
        maxDesiredWidth,
        availableHeight / aspectRatio,
        windowWidth // Ensure it doesn't exceed the viewport width
      );
      const calculatedScaleFactor = calculatedScreenWidth / maxDesiredWidth;

      setScreenWidthMobile(calculatedScreenWidth);
      setScaleFactorMobile(calculatedScaleFactor);
    }, 100);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { data: allPeriodSettings, isLoading: isLoadingAllSettings, refetch: refetchAllPeriodSettings } = useGetAllGuestPortalSettingsForPlan(planId);
  const { data: plansData, isLoading: isLoadingPlansData } = useGetPlansGuestPortalSettings(brandId, effectiveLocationId);
  const { mutate: updateAllGuestPortalSettingsForPlan } = useUpdateAllGuestPortalSettingsForPlan();
  const [plan, setPlan] = useState(null);

// Determine when initial page load is complete
  useEffect(() => {
    if (!isLoadingOrgDetails && 
        !isStatusLoading && 
        !isLoadingAllSettings && 
        !isLoadingPlansData && 
        effectiveLocationId) {
      setIsPageLoading(false);
    }
  }, [isLoadingOrgDetails, isStatusLoading, isLoadingAllSettings, isLoadingPlansData, effectiveLocationId]);

  useEffect(() => {
    setPlan(plansData?.data?.find((p) => p.id === planId));
  }, [plansData, planId]);

  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [currentPeriodSettings, setCurrentPeriodSettings] = useState(null);

  useEffect(() => {
    if (allPeriodSettings?.data) {
      const { periodStructure, guestPortalSettings } = allPeriodSettings.data;
      // Convert period structure to periods array
      const periodsArray = Object.keys(periodStructure).map((periodName) => ({
        id: periodName,
        period: periodName,
        start_time: periodStructure[periodName].start_time,
        end_time: periodStructure[periodName].end_time,
        guest_portal_setting: guestPortalSettings[periodName]?.fields?.id || null,
        period_id: guestPortalSettings[periodName]?.fields?.period || null,
      }));

      setPeriods(periodsArray);

      // Set the first period as selected by default
      if (periodsArray.length > 0) {
        setSelectedPeriod(periodsArray[0]);
      }
    }
  }, [allPeriodSettings]);

  useEffect(() => {
    if (selectedPeriod && allPeriodSettings?.data?.guestPortalSettings) {
      const periodData = allPeriodSettings.data.guestPortalSettings[selectedPeriod.id];
      if (periodData) {
        initializeFormFields(periodData, selectedPeriod.id);
      }
    }
  }, [selectedPeriod, allPeriodSettings]);

  const [emailFlag, setEmailFlag] = useState(null);
  const [ageFlag, setAgeFlag] = useState(null);
  const [genderFlag, setGenderFlag] = useState(null);
  const [dobFlag, setDobFlag] = useState(null);
  const [socialLoginFlag, setSocialLoginFlag] = useState(null);
  const [firstNameFlag, setFirstNameFlag] = useState(null);
  const [lastNameFlag, setLastNameFlag] = useState(null);
  const [phoneNumberFlag, setPhoneNumberFlag] = useState(null);
  const [organisationFlag, setOrganisationFlag] = useState(null);
  const [organisationRequiredFlag, setOrganisationRequiredFlag] = useState(false);
  const [scratchCardFlag, setScratchCardFlag] = useState(null);
  const [scratchCardAreaColor, setScratchCardAreaColor] = useState("#aqua");
  const [scratchCardBackgroundColor, setScratchCardBackgroundColor] = useState("white");

  const [logoUpload, setLogoUpload] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFileName, setLogoFileName] = useState("");
  const logoInputRef = useRef(null);
  const [deleteLogo, setDeleteLogo] = useState(false);

  const [backgroundImageUpload, setBackgroundImageUpload] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [backgroundFileName, setBackgroundFileName] = useState("");
  const backgroundInputRef = useRef(null);
  const [deleteBackgroundImage, setDeleteBackgroundImage] = useState(false);
  const [backgroundOverlayColor, setBackgroundOverlayColor] = useState("#000000");
  const [backgroundOverlayOpacity, setBackgroundOverlayOpacity] = useState(50);

  const [landingBrandingFlag, setLandingBrandingFlag] = useState(false);
  const [landingLogoUpload, setLandingLogoUpload] = useState(null);
  const [landingLogoPreview, setLandingLogoPreview] = useState(null);
  const [landingLogoFileName, setLandingLogoFileName] = useState("");
  const landingLogoInputRef = useRef(null);
  const [deleteLandingLogo, setDeleteLandingLogo] = useState(false);
  const [landingBackgroundImageUpload, setLandingBackgroundImageUpload] = useState(null);
  const [landingBackgroundPreview, setLandingBackgroundPreview] = useState(null);
  const [landingBackgroundFileName, setLandingBackgroundFileName] = useState("");
  const landingBackgroundInputRef = useRef(null);
  const [deleteLandingBackgroundImage, setDeleteLandingBackgroundImage] = useState(false);
  const [landingBackgroundOverlayColor, setLandingBackgroundOverlayColor] = useState("#000000");
  const [landingBackgroundOverlayOpacity, setLandingBackgroundOverlayOpacity] = useState(50);

  const [buttonText, setButtonText] = useState("Connect to the internet");
  const [privacyAcceptButtonText, setPrivacyAcceptButtonText] = useState("Accept & Connect");
  const [privacyDeclineButtonText, setPrivacyDeclineButtonText] = useState("Connect");
  const [buttonColor, setButtonColor] = useState("#3807D9");
  const [buttonTextColor, setButtonTextColor] = useState("#FFFFFF");
  const [fontFamily, setFontFamily] = useState(DEFAULT_PORTAL_FONT);
  const [logoSize, setLogoSize] = useState(DEFAULT_LOGO_SIZE);
  const [landingLogoSize, setLandingLogoSize] = useState(DEFAULT_LOGO_SIZE);

  const [message, setMessage] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");

  const [segmentView, setSegmentView] = useState("page");
  const [activeTab, setActiveTab] = useState("branding");

  const [showLinks, setShowLinks] = useState(false);
  const [links, setLinks] = useState([]);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerTitle, setBannerTitle] = useState("");
  const [bannerDescription, setBannerDescription] = useState("");
  const [bannerLinkURL, setBannerLinkURL] = useState("");
  const [bannerBackgroundColor, setBannerBackgroundColor] = useState("#000000");
  const [bannerTextColor, setBannerTextColor] = useState("#FFFFFF");
  const [bannerImagePreview, setBannerImagePreview] = useState(null);
  const [bannerImageFileName, setBannerImageFileName] = useState("");
  const bannerInputRef = useRef(null);
  const [deleteBannerImage, setDeleteBannerImage] = useState(false);

  const [privacyType, setPrivacyType] = useState("auto");
  const [landingType, setLandingType] = useState("page");

  const brandLocations = locationsData ? locationsData.filter((loc) => loc.brand_id === (isBrandRoute ? paramId : null)) : [];
  const canHaveUniqueUrls = isBrandRoute && brandLocations?.length > 1;

  // -- Link state helpers: always use per-period keys --
  const getLinksDataForPeriod = (periodId) => {
    const changes = periodChanges[periodId] || {};
    
    if (changes.links_data) {
      return changes.links_data;
    }
    // fallback to server data
    const periodData = allPeriodSettings?.data?.guestPortalSettings?.[periodId];
    if (periodData && periodData.links) {
      return periodData.links;
    }
    return [];
  };

  // --- Banner state helpers: always use per-period keys ---
  const getBannerDataForPeriod = (periodId) => {
    const changes = periodChanges[periodId] || {};
    if (changes.banner_data) {
      const banner = { ...changes.banner_data };
      // Only include image/image_filename if uploading or deleting
      if (changes.banner_image_upload) {
        // New image is being uploaded (handled via FormData, so just include filename for preview)
        banner.image_filename = changes.banner_image_file_name;
        // Do not include 'image' in JSON payload; file is sent via FormData
        delete banner.image;
      } else if (changes.delete_banner_image) {
        // Explicitly deleting the image
        banner.delete_image = true;
        // Do not include 'image' or 'image_filename' in JSON payload
        delete banner.image;
        delete banner.image_filename;
      } else {
        // Not changing image, so remove these fields if present
        delete banner.image;
        delete banner.image_filename;
        delete banner.delete_image;
      }
      return banner;
    }
    // fallback to server data
    const periodData = allPeriodSettings?.data?.guestPortalSettings?.[periodId];
    if (periodData && periodData.banners && periodData.banners.length > 0) {
      return periodData.banners[0];
    }
    return null;
  };

  // --- Update initializeFormFields to always use per-period banner state ---
  const initializeFormFields = (periodData, periodId) => {
    if (!periodData?.fields) return;

    setIsInitializedForm(false);

    // 1. Start with server data
    let fields = { ...periodData.fields };
    let banners = periodData.banners || [];
    let links = periodData.links || [];

    // 2. If there are unsaved changes for this period, merge them in
    const changes = periodChanges[periodId] || {};
    fields = { ...fields, ...changes };

    // Use local preview/upload if present in periodChanges
    setLogoPreview(changes.logo_preview !== undefined ? changes.logo_preview : fields.logo);
    setLogoUpload(changes.logo_upload !== undefined ? changes.logo_upload : null);
    setLogoFileName(fields.logo_filename || "");
    setBackgroundPreview(changes.background_preview !== undefined ? changes.background_preview : fields.background_image);
    setBackgroundImageUpload(changes.background_image_upload !== undefined ? changes.background_image_upload : null);
    setBackgroundFileName(fields.background_image_filename || "");
    setLandingLogoPreview(changes.landing_logo_preview !== undefined ? changes.landing_logo_preview : fields.landing_logo);
    setLandingLogoUpload(changes.landing_logo_upload !== undefined ? changes.landing_logo_upload : null);
    setLandingLogoFileName(fields.landing_logo_filename || "");
    setLandingBackgroundPreview(changes.landing_background_preview !== undefined ? changes.landing_background_preview : fields.landing_background_image);
    setLandingBackgroundImageUpload(changes.landing_background_image_upload !== undefined ? changes.landing_background_image_upload : null);
    setLandingBackgroundFileName(fields.landing_background_image_filename || "");
    setBackgroundOverlayColor(changes.background_overlay_colour !== undefined ? changes.background_overlay_colour : fields.background_overlay_colour || "#000000");
    setBackgroundOverlayOpacity(changes.background_overlay_opacity !== undefined ? changes.background_overlay_opacity : parseFloat(fields.background_overlay_opacity || "0.5") * 100);
    setLandingBackgroundOverlayColor(changes.landing_background_overlay_colour !== undefined ? changes.landing_background_overlay_colour : fields.landing_background_overlay_colour || "#000000");
    setLandingBackgroundOverlayOpacity(changes.landing_background_overlay_opacity !== undefined ? changes.landing_background_overlay_opacity : parseFloat(fields.landing_background_overlay_opacity || "0.5") * 100);
    setButtonText(changes.button_text !== undefined ? changes.button_text : fields.button_text || "Connect to the internet");
    setPrivacyAcceptButtonText(changes.privacy_accept_button_text !== undefined ? changes.privacy_accept_button_text : fields.privacy_accept_button_text || "Accept & Connect");
    setPrivacyDeclineButtonText(changes.privacy_decline_button_text !== undefined ? changes.privacy_decline_button_text : fields.privacy_decline_button_text || "Connect");
    setButtonColor(changes.button_colour !== undefined ? changes.button_colour : fields.button_colour || "#3807D9");
    setButtonTextColor(changes.button_text_colour !== undefined ? changes.button_text_colour : fields.button_text_colour || "#FFFFFF");
    setFontFamily(changes.font_family !== undefined ? changes.font_family : fields.font_family || DEFAULT_PORTAL_FONT);
    setLogoSize(normalizeLogoSize(changes.logo_size ?? fields.logo_size));
    setLandingLogoSize(normalizeLogoSize(changes.landing_logo_size ?? fields.landing_logo_size));
    setMessage(fields.message || "");
    setPrivacyPolicy(fields.privacy_policy || "");
    setRedirectUrl(fields.redirect_url || "");
    setEmailFlag(fields.email_flag || false);
    setAgeFlag(fields.age_flag || false);
    setDobFlag(fields.dob_flag || false);
    setGenderFlag(fields.gender_flag || false);
    setFirstNameFlag(fields.first_name_flag || false);
    setLastNameFlag(fields.last_name_flag || false);
    setPhoneNumberFlag(fields.phone_number_flag || false);
    setOrganisationFlag(fields.organisation_flag || false);
    setOrganisationRequiredFlag(fields.organisation_required_flag !== undefined ? fields.organisation_required_flag : false);
    setScratchCardFlag(fields.scratch_card_flag || false);
    setScratchCardAreaColor(fields.scratch_card_area_colour || "aqua");
    setScratchCardBackgroundColor(fields.scratch_card_background_colour || "green");
    setSocialLoginFlag(fields.social_login_flag || false);
    setShowLinks(fields.links_flag || false);
    setShowBanner(fields.banner_flag || false);
    setLandingBrandingFlag(fields.landing_branding_flag || false);
    setLandingType(fields.landing_type || "page");
    setSegmentView(fields.landing_type || "page");
    setPrivacyType(fields.privacy_type || "auto");

    // Banner: always use per-period state
    const banner = getBannerDataForPeriod(periodId);
    if (banner) {
      setBannerTitle(banner.title || "");
      setBannerDescription(banner.description || "");
      setBannerLinkURL(banner.banner_url || "");
      setBannerBackgroundColor(banner.color || "#000000");
      setBannerTextColor(banner.text_color || "#FFFFFF");
      setBannerImagePreview(banner.image || null);
      setBannerImageFileName(banner.image_filename || "");
    } else {
      setBannerTitle("");
      setBannerDescription("");
      setBannerLinkURL("");
      setBannerBackgroundColor("#000000");
      setBannerTextColor("#FFFFFF");
      setBannerImagePreview(null);
      setBannerImageFileName("");
    }

    // Links: use per-period state like banners
    const periodLinks = getLinksDataForPeriod(periodId);
    setLinks(periodLinks || []);

    // Reset dirty state and upload states (except logo/background/banner/link, which are now handled above)
    setIsDirty(false);
    setDeleteLogo(false);
    setDeleteBackgroundImage(false);
    setDeleteLandingLogo(false);
    setDeleteLandingBackgroundImage(false);
    setDeleteBannerImage(false);
    setIsInitializedForm(true);
  };

  // Handle period selection
  const handlePeriodClick = (period) => {
    setSelectedPeriod(period);
  };

  // Enhanced form fields change handler with tracking
  const handleCpFormFieldsChange = (values) => {
    const ageChecked = values.includes("age");
    const emailChecked = values.includes("email");
    const genderChecked = values.includes("gender");
    const socialLoginChecked = values.includes("social_login");
    const firstNameChecked = values.includes("first-name");
    const lastNameChecked = values.includes("last-name");
    const phoneNumberChecked = values.includes("phone-number");
    const organisationChecked = values.includes("organisation");

    // Update state
    setEmailFlag(emailChecked);
    setAgeFlag(ageChecked);
    setGenderFlag(genderChecked);
    setSocialLoginFlag(socialLoginChecked);
    setFirstNameFlag(firstNameChecked);
    setLastNameFlag(lastNameChecked);
    setPhoneNumberFlag(phoneNumberChecked);
    if (isDotdigitalActive()) {
      setOrganisationFlag(organisationChecked);
    }
    if (!organisationChecked) {
      setOrganisationRequiredFlag(false);
    }

    // ALWAYS: if age is unchecked, uncheck DOB (regardless of integration status)
    if (!ageChecked) {
      setDobFlag(false);
      trackPeriodChange(selectedPeriod.id, "dob_flag", false);
    }

    // When an integration is active, ensure age and DOB mirror each other when age is checked
    if (isIntegrationActive() && ageChecked) {
      setDobFlag(true);
      trackPeriodChange(selectedPeriod.id, "dob_flag", true);
    }

    // Track all field changes
    const fieldChanges = {
      email_flag: emailChecked,
      age_flag: ageChecked,
      gender_flag: genderChecked,
      social_login_flag: socialLoginChecked,
      first_name_flag: firstNameChecked,
      last_name_flag: lastNameChecked,
      phone_number_flag: phoneNumberChecked,
    };
    if (isDotdigitalActive()) {
      fieldChanges.organisation_flag = organisationChecked;
      if (!organisationChecked) {
        fieldChanges.organisation_required_flag = false;
      }
    }

    Object.entries(fieldChanges).forEach(([field, value]) => {
      trackPeriodChange(selectedPeriod.id, field, value);
    });
  };

  function getButtonTextColorBasedOnBackground(buttonBackgroundColor) {
    buttonBackgroundColor = buttonBackgroundColor.replace("#", "");
    // Converting the hex color to RGB
    const r = parseInt(buttonBackgroundColor.substring(0, 2), 16) / 255;
    const g = parseInt(buttonBackgroundColor.substring(2, 4), 16) / 255;
    const b = parseInt(buttonBackgroundColor.substring(4, 6), 16) / 255;

    // Calculating luminance using the formula
    const luminance =
      0.2126 * (r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)) +
      0.7152 * (g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)) +
      0.0722 * (b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4));

    // If luminance is higher than 0.5, using black text; otherwise, white text
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  }

  useEffect(() => {
    const calculatedTextColor = getButtonTextColorBasedOnBackground(buttonColor);
    setButtonTextColor(calculatedTextColor);
    // Track the calculated button text color change
    if (selectedPeriod?.id) {
      trackPeriodChange(selectedPeriod.id, "button_text_colour", calculatedTextColor);
    }
  }, [buttonColor, selectedPeriod?.id]);

  function colorConversion(inputColour, opacity) {
    const alpha = Math.round(opacity * 2.55)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
    return `${inputColour}${alpha}`;
  }

  useEffect(() => {
    colorConversion(backgroundOverlayColor, backgroundOverlayOpacity);
  }, [backgroundOverlayColor, backgroundOverlayOpacity]);

  useEffect(() => {
    colorConversion(landingBackgroundOverlayColor, landingBackgroundOverlayOpacity);
  }, [landingBackgroundOverlayColor, landingBackgroundOverlayOpacity]);

  // Helper function to add https:// to a URL if it doesn't have it
  const addHttpsIfNeeded = (url) => {
    if (url && !url.match(/^https?:\/\//)) {
      return `https://${url}`;
    }
    return url;
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) {
      setToastMessage("Maximum file size of 1MB");
      return;
    }
    setLogoUpload(file);
    setLogoFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      // Track in periodChanges
      trackPeriodChange(selectedPeriod.id, "logo_upload", file);
      trackPeriodChange(selectedPeriod.id, "logo_preview", reader.result);
    };
    reader.readAsDataURL(file);
    setToastMessage("Uploaded");
    setDeleteLogo(false);
    trackPeriodChange(selectedPeriod.id, "delete_logo", false);
  };

  const handleLandingLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) {
      setToastMessage("Maximum file size of 1MB");
      return;
    }
    setLandingLogoUpload(file);
    setLandingLogoFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLandingLogoPreview(reader.result);
      // Track in periodChanges
      trackPeriodChange(selectedPeriod.id, "landing_logo_upload", file);
      trackPeriodChange(selectedPeriod.id, "landing_logo_preview", reader.result);
    };
    reader.readAsDataURL(file);
    setToastMessage("Uploaded");
    setDeleteLandingLogo(false);
    trackPeriodChange(selectedPeriod.id, "delete_landing_logo", false);
  };

  const handleLogoClear = () => {
    setLogoUpload(null);
    setLogoPreview(null);
    setLogoFileName("");
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    setDeleteLogo(true);
    // Track in periodChanges
    trackPeriodChange(selectedPeriod.id, "logo_upload", null);
    trackPeriodChange(selectedPeriod.id, "logo_preview", null);
    trackPeriodChange(selectedPeriod.id, "delete_logo", true);
  };

  const handleLandingLogoClear = () => {
    setLandingLogoUpload(null);
    setLandingLogoPreview(null);
    setLandingLogoFileName("");
    if (landingLogoInputRef.current) {
      landingLogoInputRef.current.value = "";
    }
    setDeleteLandingLogo(true);
    // Track in periodChanges
    trackPeriodChange(selectedPeriod.id, "landing_logo_upload", null);
    trackPeriodChange(selectedPeriod.id, "landing_logo_preview", null);
    trackPeriodChange(selectedPeriod.id, "delete_landing_logo", true);
  };

  const triggerLogoInput = () => {
    if (logoInputRef.current) {
      logoInputRef.current.click();
    }
  };

  const triggerLandingLogoInput = () => {
    if (landingLogoInputRef.current) {
      landingLogoInputRef.current.click();
    }
  };

  const handleBackgroundImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) {
      setToastMessage("Maximum file size of 1MB");
      return;
    }
    setBackgroundImageUpload(file);
    setBackgroundFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setBackgroundPreview(reader.result);
      // Track in periodChanges
      trackPeriodChange(selectedPeriod.id, "background_image_upload", file);
      trackPeriodChange(selectedPeriod.id, "background_preview", reader.result);
    };
    reader.readAsDataURL(file);
    setToastMessage("Uploaded");
    setDeleteBackgroundImage(false);
    trackPeriodChange(selectedPeriod.id, "delete_background_image", false);
  };

  const handleLandingBackgroundImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) {
      setToastMessage("Maximum file size of 1MB");
      return;
    }
    setLandingBackgroundImageUpload(file);
    setLandingBackgroundFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLandingBackgroundPreview(reader.result);
      // Track in periodChanges
      trackPeriodChange(selectedPeriod.id, "landing_background_image_upload", file);
      trackPeriodChange(selectedPeriod.id, "landing_background_preview", reader.result);
    };
    reader.readAsDataURL(file);
    setToastMessage("Uploaded");
    setDeleteLandingBackgroundImage(false);
    trackPeriodChange(selectedPeriod.id, "delete_landing_background_image", false);
  };

  const handleBackgroundImageClear = () => {
    setBackgroundImageUpload(null);
    setBackgroundPreview(null);
    setBackgroundFileName("");
    if (backgroundInputRef.current) {
      backgroundInputRef.current.value = "";
    }
    setDeleteBackgroundImage(true);
    // Track in periodChanges
    trackPeriodChange(selectedPeriod.id, "background_image_upload", null);
    trackPeriodChange(selectedPeriod.id, "background_preview", null);
    trackPeriodChange(selectedPeriod.id, "delete_background_image", true);
  };

  const handleLandingBackgroundImageClear = () => {
    setLandingBackgroundImageUpload(null);
    setLandingBackgroundPreview(null);
    setLandingBackgroundFileName("");
    if (landingBackgroundInputRef.current) {
      landingBackgroundInputRef.current.value = "";
    }
    setDeleteLandingBackgroundImage(true);
    // Track in periodChanges
    trackPeriodChange(selectedPeriod.id, "landing_background_image_upload", null);
    trackPeriodChange(selectedPeriod.id, "landing_background_preview", null);
    trackPeriodChange(selectedPeriod.id, "delete_landing_background_image", true);
  };

  const triggerBackgroundInput = () => {
    if (backgroundInputRef.current) {
      backgroundInputRef.current.click();
    }
  };

  const triggerLandingBackgroundInput = () => {
    if (landingBackgroundInputRef.current) {
      landingBackgroundInputRef.current.click();
    }
  };

  // Enhanced change handlers that track changes
  const handleInputMessageChange = (value) => {
    setMessage(value);
    trackPeriodChange(selectedPeriod.id, "message", value);
  };

  const handlePrivacyPolicyChange = (value) => {
    setPrivacyPolicy(value);
    trackPeriodChange(selectedPeriod.id, "privacy_policy", value);
  };

  const handleInputRedirectUrlChange = (value) => {
    setRedirectUrl(addHttpsIfNeeded(value));
    trackPeriodChange(selectedPeriod.id, "redirect_url", addHttpsIfNeeded(value));
  };

  // --- Update buildCurrentPeriodData to format banner_url ONLY when sending to backend ---
  const buildCurrentPeriodData = (periodId) => {
    // Remove start_time and end_time if present in currentPeriodSettings.fields
    const { start_time, end_time, ...fieldsWithoutTimes } = currentPeriodSettings?.fields || {};
    const changes = periodChanges[periodId] || {};
    // Use banner_data from periodChanges if present
    let bannerArr = [];
    if (changes.banner_data) {
      // Format banner_url only for backend
      bannerArr = [{
        ...changes.banner_data,
        banner_url: addHttpsIfNeeded(changes.banner_data.banner_url),
      }];
    } else if (currentPeriodSettings?.banners && currentPeriodSettings.banners.length > 0) {
      bannerArr = [{
        ...currentPeriodSettings.banners[0],
        banner_url: addHttpsIfNeeded(currentPeriodSettings.banners[0].banner_url),
      }];
    }
    const banner = getBannerDataForPeriod(periodId);
    // Also ensure the banner_url in the previewed banner is formatted for backend only
    const formattedBanner = banner && showBanner ? [{ ...banner, banner_url: addHttpsIfNeeded(banner.banner_url) }] : [];

    const periodLinks = getLinksDataForPeriod(periodId);
    const formattedLinks = showLinks ? periodLinks.map((link, index) => {
      const formattedLink = {
        id: (link.id && typeof link.id === 'string' && link.id.startsWith("temp_")) ? null : link.id,
        label: link.label,
        link_url: addHttpsIfNeeded(link.link_url),
        unique_url_flag: link.unique_url_flag,
        location_link_urls: link.location_link_urls || {},
        order: index,
      };
      
      // Preserve icon file objects - don't lose them during formatting
      if (link.icon_file) {
        formattedLink.icon_file = link.icon_file;
      }
      if (link.iconUpload) {
        formattedLink.iconUpload = link.iconUpload;
      }
      if (link.icon_preview) {
        formattedLink.icon_preview = link.icon_preview;
      }
      if (link.icon_filename) {
        formattedLink.icon_filename = link.icon_filename;
      }
      if (link.delete_icon) {
        formattedLink.delete_icon = link.delete_icon;
      }
      
      return formattedLink;
    }) : [];

    return {
      fields: {
        ...fieldsWithoutTimes,
        // All current form fields
        background_overlay_colour: backgroundOverlayColor,
        scratch_card_area_colour: scratchCardAreaColor,
        scratch_card_background_colour: scratchCardBackgroundColor,
        scratch_card_flag: scratchCardFlag,
        background_overlay_opacity: (backgroundOverlayOpacity / 100).toString(),
        landing_background_overlay_colour: landingBackgroundOverlayColor,
        landing_background_overlay_opacity: (landingBackgroundOverlayOpacity / 100).toString(),
        button_text: buttonText,
        privacy_accept_button_text: privacyAcceptButtonText,
        privacy_decline_button_text: privacyDeclineButtonText,
        button_colour: buttonColor,
        button_text_colour: buttonTextColor,
        font_family: fontFamily,
        logo_size: logoSize,
        landing_logo_size: landingLogoSize,
        message: message,
        privacy_policy: privacyPolicy,
        redirect_url: redirectUrl,
        email_flag: emailFlag,
        age_flag: ageFlag,
        dob_flag: dobFlag,
        gender_flag: genderFlag,
        first_name_flag: firstNameFlag,
        last_name_flag: lastNameFlag,
        phone_number_flag: phoneNumberFlag,
        organisation_flag: organisationFlag,
        organisation_required_flag: organisationRequiredFlag,
        social_login_flag: socialLoginFlag,
        banner_flag: showBanner,
        landing_branding_flag: landingBrandingFlag,
        links_flag: showLinks,
        privacy_type: privacyType,
        landing_type: landingType,
        delete_logo: deleteLogo,
        delete_landing_logo: deleteLandingLogo,
        delete_background_image: deleteBackgroundImage,
        delete_landing_background_image: deleteLandingBackgroundImage,
      },
      links: showLinks && formattedLinks.length > 0 ? formattedLinks : [],
      banners: showBanner && formattedBanner.length > 0 ? formattedBanner : [],
    };
  };

  // --- Update buildChangedFieldsOnly to always use per-period banner ---
  const buildChangedFieldsOnly = (periodId) => {
    const changes = periodChanges[periodId] || {};
    const result = { fields: {}, links: null, banners: null };
    // Only include changed fields, but never start_time or end_time
    Object.keys(changes).forEach((field) => {
      if (field === "start_time" || field === "end_time") return;
      // Map camelCase keys to snake_case for backend
      const fieldMap = {
        logoUpload: "logo_upload",
        logoPreview: "logo_preview",
        backgroundImageUpload: "background_image_upload",
        backgroundPreview: "background_preview",
        landingLogoUpload: "landing_logo_upload",
        landingLogoPreview: "landing_logo_preview",
        landingBackgroundImageUpload: "landing_background_image_upload",
        landingBackgroundPreview: "landing_background_preview",
        bannerImageUpload: "banner_image_upload",
        bannerImagePreview: "banner_image_preview",
        bannerImageFileName: "banner_image_file_name",
        linksWithIcons: "links_with_icons",
        bannerData: "banner_data",
        scratchCardFlag: "scratch_card_flag",
        showBanner: "banner_flag",
        showLinks: "links_flag",
      };
      const snakeField = fieldMap[field] || field;
      // if (snakeField === "links_data" || field === "links_flag") {
      if (snakeField === "links_data") {
        // Handle links changes
        const periodLinks = getLinksDataForPeriod(periodId);
        if (showLinks && periodLinks.length > 0) {
          result.links = periodLinks.map((link, index) => {
            const formattedLink = {
              id: (link.id && typeof link.id === 'string' && link.id.startsWith("temp_")) ? null : link.id,
              label: link.label,
              link_url: addHttpsIfNeeded(link.link_url),
              unique_url_flag: link.unique_url_flag,
              location_link_urls: link.location_link_urls || {},
              order: index,
              color: link.color || "#000000",
              text_color: link.text_color || "#FFFFFF",
            };
            
            // Preserve icon file objects
            if (link.icon_file) {
              formattedLink.icon_file = link.icon_file;
            }
            if (link.iconUpload) {
              formattedLink.iconUpload = link.iconUpload;
            }
            if (link.icon_preview) {
              formattedLink.icon_preview = link.icon_preview;
            }
            if (link.icon_filename) {
              formattedLink.icon_filename = link.icon_filename;
            }
            if (link.delete_icon) {
              formattedLink.delete_icon = link.delete_icon;
            }
            
            return formattedLink;
          });
        } else {
          result.links = [];
        }
      } else if (snakeField.startsWith("banner_") && snakeField !== "banner_flag") {
        // Handle banner changes
        const banner = getBannerDataForPeriod(periodId);
        if (banner && showBanner) {
          result.banners = [banner];
        }
      } else {
        // Handle field changes
        result.fields[snakeField] = changes[field];
      }
    });
    return result;
  };

  // Update handleFileUploads to use period.period as the key for file uploads
  const handleFileUploads = (formData, periods) => {
    periods.forEach((period) => {
      const changes = periodChanges[period.period] || {};

      if (changes.logo_upload) {
        formData.append(`${period.period}_logo_image`, changes.logo_upload);
      }
      if (changes.background_image_upload) {
        formData.append(`${period.period}_background_image`, changes.background_image_upload);
      }
      if (changes.landing_logo_upload) {
        formData.append(`${period.period}_landing_logo_image`, changes.landing_logo_upload);
      }
      if (changes.landing_background_image_upload) {
        formData.append(`${period.period}_landing_background_image`, changes.landing_background_image_upload);
      }
      if (changes.banner_image_upload) {
        formData.append(`${period.period}_banner_image`, changes.banner_image_upload);
      }
      
      // Debug links data
      const periodLinks = getLinksDataForPeriod(period.period);
      
      periodLinks.forEach((link, index) => {
        
        // Check for icon_file (from LinkEditor) or iconUpload
        if (link.icon_file || link.iconUpload) {
          const iconFile = link.icon_file || link.iconUpload;
          const fileKey = `${period.period}_link_${index}_icon`;
          formData.append(fileKey, iconFile);
        } else {
          console.warn(`No icon file found for link ${index} in period ${period.period}`);
        }
      });
    });
  };

  // Add to state
  const [deletedPeriods, setDeletedPeriods] = useState([]);
  const [resetUnsavedPeriodChanges, setResetUnsavedPeriodChanges] = useState(false);

  // Helper: get action for a period
  const getPeriodAction = (period) => {
    if (period.action) return period.action;
    if (newPeriods.includes(period.id)) return "add";
    return "update";
  };

  // Build PATCH payload as per new requirements
  const buildPatchPayload = () => {
    // 1. Build guestPortalSettings (unchanged)
    const guestPortalSettings = {};
    periods.forEach((period) => {
      if (getPeriodAction(period) !== "delete") {
        let periodData;
        if (getPeriodAction(period) === "add") {
          periodData = buildCurrentPeriodData(period.id);
        } else {
          periodData = buildChangedFieldsOnly(period.id);
        }
        if (periodData.banners && Array.isArray(periodData.banners)) {
          periodData.banners = periodData.banners.map(banner => ({
            ...banner,
            banner_url: addHttpsIfNeeded(banner.banner_url),
          }));
        }
        guestPortalSettings[period.id] = periodData;
      }
    });
    // 2. Build metadata (optional, can be omitted if not needed)
    const metadata = {
      timestamp: new Date().toISOString(),
      totalPeriods: periods.length,
      periodNames: periods.map((p) => p.id),
    };
    // 3. Return the full payload (without periodStructure)
    return {
      planId,
      metadata,
      guestPortalSettings,
    };
  };

  // Update handleSubmit to use the periods array for file uploads
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!allPeriodSettings?.data) return;
    
    setIsSaveChangesLoading(true);
    
    try {
      const payload = buildPatchPayload();
      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));
      // Find all periods with changed file uploads
      const changedPeriods = periods.filter((period) => {
        const changes = periodChanges[period.period] || {};
        return changes.logo_upload || changes.background_image_upload || changes.landing_logo_upload || changes.landing_background_image_upload || changes.banner_image_upload;
      });
      handleFileUploads(formData, changedPeriods);
      
      await updateAllGuestPortalSettingsForPlan({
        planId: planId,
        formData: formData,
      });
      const remindAboutIntegrations = didDataFieldsChange(periodChanges);
      setToastMessage("Settings saved successfully");
      setPeriodChanges({});
      setNewPeriods([]);
      setDeletedPeriods([]);
      resetUploadStates();
      setResetUnsavedPeriodChanges(true);
      setTimeout(() => setResetUnsavedPeriodChanges(false), 100);
      setTimeout(() => {
        setIsSaveChangesLoading(false);
        setIsDirty(false);
      }, 100);
      if (remindAboutIntegrations) {
        setShowIntegrationsReminder(true);
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setToastMessage("Failed to save settings");
      setIsSaveChangesLoading(false);
    }
  };

  // Reset function
  const resetUploadStates = () => {
    setLogoUpload(null);
    setBackgroundImageUpload(null);
    setDeleteLogo(false);
    setDeleteBackgroundImage(false);
    setLandingLogoUpload(null);
    setLandingBackgroundImageUpload(null);
    setDeleteLandingLogo(false);
    setDeleteLandingBackgroundImage(false);
    setDeleteBannerImage(false);
  };

// Helper function to get original settings for a period
const getOriginalPeriodSettings = (periodId) => {
  return allPeriodSettings?.data?.guestPortalSettings?.[periodId];
};

// Helper function to compare current state with original for a specific period
const hasPeriodChanges = (periodId) => {
  const original = getOriginalPeriodSettings(periodId);
  if (!original?.fields) return false;

  const changes = periodChanges[periodId] || {};
  
  // If there are any tracked changes for this period, check if they differ from original
  if (Object.keys(changes).length > 0) {
    for (const [field, value] of Object.entries(changes)) {
      // Skip period structure fields
      if (field === "start_time" || field === "end_time") continue;
      
      // Handle special cases for different field types
      let originalValue;
      
      switch (field) {
        case "logo_size":
        case "landing_logo_size":
          originalValue = normalizeLogoSize(original.fields[field]);
          break;
        case "background_overlay_opacity":
          originalValue = parseFloat(original.fields.background_overlay_opacity || "0.5") * 100;
          break;
        case "links_data":
          originalValue = original.links || [];
          break;
        case "banner_data":
          originalValue = original.banners?.[0] || null;
          break;
        case "logo_preview":
          originalValue = original.fields.logo;
          break;
        case "background_preview":
          originalValue = original.fields.background_image;
          break;
        case "landing_logo_preview":
          originalValue = original.fields.landing_logo;
          break;
        case "landing_background_preview":
          originalValue = original.fields.landing_background_image;
          break;
        default:
          originalValue = original.fields[field];
      }
      
      // Handle boolean flags (ensure consistent type comparison)
      if (typeof originalValue === 'boolean' || typeof value === 'boolean') {
        if (Boolean(originalValue) !== Boolean(value)) return true;
      }
      // Handle arrays (for links)
      else if (Array.isArray(originalValue) || Array.isArray(value)) {
        if (JSON.stringify(originalValue || []) !== JSON.stringify(value || [])) return true;
      }
      // Handle objects (for banner data)
      else if (typeof originalValue === 'object' || typeof value === 'object') {
        if (JSON.stringify(originalValue || {}) !== JSON.stringify(value || {})) return true;
      }
      // Handle regular values
      else if (originalValue !== value) {
        return true;
      }
    }
  }
  
  // Also check current form state against original for the selected period
  if (periodId === selectedPeriod?.id) {
    const checks = [
      backgroundOverlayColor !== (original.fields.background_overlay_colour || "#000000"),
      scratchCardAreaColor !== (original.fields.scratch_card_area_colour || "#aqua"),
      scratchCardBackgroundColor !== (original.fields.scratch_card_background_colour || "white"),
      scratchCardFlag !== (original.fields.scratch_card_flag || false),
      Math.round(backgroundOverlayOpacity) !== Math.round(parseFloat(original.fields.background_overlay_opacity || "0.5") * 100),
      landingBackgroundOverlayColor !== (original.fields.landing_background_overlay_colour || "#000000"),
      Math.round(landingBackgroundOverlayOpacity) !== Math.round(parseFloat(original.fields.landing_background_overlay_opacity || "0.5") * 100),
      buttonText !== (original.fields.button_text || "Connect to the internet"),
      privacyAcceptButtonText !== (original.fields.privacy_accept_button_text || "Accept & Connect"),
      privacyDeclineButtonText !== (original.fields.privacy_decline_button_text || "Connect"),
      buttonColor !== (original.fields.button_colour || "#3807D9"),
      buttonTextColor !== (original.fields.button_text_colour || "#FFFFFF"),
      fontFamily !== (original.fields.font_family || DEFAULT_PORTAL_FONT),
      logoSize !== normalizeLogoSize(original.fields.logo_size),
      landingLogoSize !== normalizeLogoSize(original.fields.landing_logo_size),
      message !== (original.fields.message || ""),
      privacyPolicy !== (original.fields.privacy_policy || ""),
      redirectUrl !== (original.fields.redirect_url || ""),
      emailFlag !== (original.fields.email_flag || false),
      ageFlag !== (original.fields.age_flag || false),
      dobFlag !== (original.fields.dob_flag || false),
      genderFlag !== (original.fields.gender_flag || false),
      firstNameFlag !== (original.fields.first_name_flag || false),
      lastNameFlag !== (original.fields.last_name_flag || false),
      phoneNumberFlag !== (original.fields.phone_number_flag || false),
      organisationFlag !== (original.fields.organisation_flag || false),
      organisationRequiredFlag !== (original.fields.organisation_required_flag !== undefined ? original.fields.organisation_required_flag : false),
      socialLoginFlag !== (original.fields.social_login_flag || false),
      showLinks !== (original.fields.links_flag || false),
      showBanner !== (original.fields.banner_flag || false),
      landingBrandingFlag !== (original.fields.landing_branding_flag || false),
      privacyType !== (original.fields.privacy_type || "auto"),
      landingType !== (original.fields.landing_type || "page"),
      bannerTitle !== (original.banners?.[0]?.title || ""),
      bannerDescription !== (original.banners?.[0]?.description || ""),
      bannerLinkURL !== (original.banners?.[0]?.banner_url || ""),
      bannerBackgroundColor !== (original.banners?.[0]?.color || "#000000"),
      bannerTextColor !== (original.banners?.[0]?.text_color || "#FFFFFF"),
      JSON.stringify(links || []) !== JSON.stringify(original.links || []),
      logoUpload !== null,
      backgroundImageUpload !== null,
      deleteLogo,
      deleteBackgroundImage,
      landingLogoUpload !== null,
      landingBackgroundImageUpload !== null,
      deleteLandingLogo,
      deleteLandingBackgroundImage,
      deleteBannerImage
    ];
    
    if (checks.some(check => check)) return true;
  }
  
  return false;
};

// Main function to check if any period has changes
const hasAnyPeriodChanges = () => {
  if (!periods || periods.length === 0) return false;
  
  return periods.some(period => hasPeriodChanges(period.id));
};

// Replace the existing useEffect for isDirty with this:
useEffect(() => {
  // If we're not initialized, make sure prev flag is reset and keep clean
  if (!isInitializedForm) {
    prevIsInitializedRef.current = false;
    setIsDirty(false);
    return;
  }

  // If we just transitioned from false -> true, skip this first run.
  // initializeFormFields should have synchronously computed and set isDirty already.
  if (isInitializedForm && !prevIsInitializedRef.current) {
    prevIsInitializedRef.current = true;
    // skip recomputing immediately after init to avoid racing with bulk setState
    return;
  }

  if (!allPeriodSettings?.data) {
    setIsDirty(false);
    return;
  }
  
  const hasChanges = hasAnyPeriodChanges();
  setIsDirty(hasChanges);
}, [
  // All the dependencies that could affect the dirty state
  allPeriodSettings,
  periods,
  selectedPeriod,
  periodChanges,
  backgroundOverlayColor,
  backgroundOverlayOpacity,
  landingBackgroundOverlayColor,
  landingBackgroundOverlayOpacity,
  buttonText,
  buttonColor,
  buttonTextColor,
  fontFamily,
  logoSize,
  landingLogoSize,
  message,
  privacyPolicy,
  scratchCardAreaColor,
  scratchCardBackgroundColor,
  scratchCardFlag,
  redirectUrl,
  emailFlag,
  ageFlag,
  dobFlag,
  genderFlag,
  firstNameFlag,
  lastNameFlag,
  phoneNumberFlag,
  organisationFlag,
  organisationRequiredFlag,
  socialLoginFlag,
  showLinks,
  showBanner,
  landingBrandingFlag,
  privacyType,
  landingType,
  bannerTitle,
  bannerDescription,
  bannerLinkURL,
  bannerBackgroundColor,
  bannerTextColor,
  links,
  logoUpload,
  backgroundImageUpload,
  deleteLogo,
  deleteBackgroundImage,
  landingLogoUpload,
  landingBackgroundImageUpload,
  deleteLandingLogo,
  deleteLandingBackgroundImage,
  deleteBannerImage
]);

// Also update the trackPeriodChange function to trigger dirty state check
const trackPeriodChange = (periodId, field, value) => {
  setPeriodChanges((prev) => ({
    ...prev,
    [periodId]: {
      ...prev[periodId],
      [field]: value,
    },
  }));
  // Remove the setIsDirty(true) call here since the useEffect will handle it
};

useEffect(() => {
  const dotdigitalIsActive = dotdigitalStatusData?.data?.is_active === true;
  if (isDotdigitalStatusLoading || dotdigitalIsActive || !organisationFlag) {
    return;
  }

  setOrganisationFlag(false);
  setOrganisationRequiredFlag(false);
  if (selectedPeriod?.id) {
    trackPeriodChange(selectedPeriod.id, "organisation_flag", false);
    trackPeriodChange(selectedPeriod.id, "organisation_required_flag", false);
  }
}, [dotdigitalStatusData, isDotdigitalStatusLoading, organisationFlag, selectedPeriod]);

  const getCheckedCount = () => {
    let count = 0;
    if (emailFlag) count++;
    if (ageFlag) count++;
    if (genderFlag) count++;
    if (firstNameFlag) count++;
    if (lastNameFlag) count++;
    if (phoneNumberFlag) count++;
    if (isDotdigitalActive() && organisationFlag) count++;
    return count;
  };

  const shouldDisableNames = !emailFlag && !phoneNumberFlag;
  const isDisabled = (current, nameField = false) => {
    const checkedCount = getCheckedCount();
    if (nameField && shouldDisableNames) return true;
    if (current) return false;
    return checkedCount >= 4;
  };

  // useEffect(() => {
  //   if (isIntegrationActive() && ageFlag) {
  //     setDobFlag(true);
  //   } else if (isIntegrationActive() && !ageFlag) {
  //     setDobFlag(false);
  //   }
  // }, [ageFlag, isAirshipActive(), isMetaActive()]);

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    d.setDate(d.getDate());
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  function formatDateMinusOne(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    d.setDate(d.getDate() - 1);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  }

  function getPlanBadgeInfo({ plan, plansData, guestPortalSettings, formatDate, formatDateMinusOne }) {
    const now = new Date();
    const nextCustomPlan = plansData?.data?.filter((p) => !p.is_main && new Date(p.start_date) > now)?.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0];

    // For main plans, check if this plan is currently active by checking if no custom plans are currently active
    const isMainPlanActive =
      plan?.is_main &&
      !plansData?.data?.some((p) => {
        if (p.is_main) return false;
        const planStart = new Date(p.start_date);
        const planEnd = new Date(p.end_date);
        return now >= planStart && now <= planEnd;
      });

    // Green dot logic - show for main plans when they're active, or for custom plans when they're currently in their date range
    let showGreenDot = false;
    if (plan?.is_main) {
      showGreenDot = isMainPlanActive;
    } else if (plan && !plan.is_main) {
      const planStart = new Date(plan.start_date);
      const planEnd = new Date(plan.end_date);
      showGreenDot = now >= planStart && now <= planEnd;
    }

    // Badge color and label
    let badgeColor = "gray";
    let badgeLabel = plan?.title || "";
    if (plan?.is_main) {
      badgeColor = "amber";
      badgeLabel = "Default";
    } else if (plan && showGreenDot) {
      badgeColor = "grass";
    }

    // Info text (the "tooltip" content in planner)
    let infoText = "";
    if (plan) {
      if (plan.is_main) {
        if (showGreenDot) {
          infoText = `${nextCustomPlan ? `Currently active until ${formatDate(nextCustomPlan.start_date)}` : "Currently active"}`;
        } else {
          infoText = `Currently inactive`;
        }
      } else if (showGreenDot) {
        infoText = `Currently active until ${formatDateMinusOne(plan.end_date)}`;
      } else {
        infoText = `Planned for ${formatDate(plan.start_date)} - ${formatDateMinusOne(plan.end_date)}`;
      }
    }

    return { showGreenDot, badgeColor, badgeLabel, infoText };
  }

  const [isEditPlanPopoverOpen, setIsEditPlanPopoverOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  // State to track which period to delete
  const [periodToDelete, setPeriodToDelete] = useState(null);

  const isCurrentPlanActive = () => {
    if (!plan) return false;
    if (plan.is_main) return true;

    if (plan.start_date && plan.end_date) {
      const now = new Date();
      const startDate = new Date(plan.start_date);
      const endDate = new Date(plan.end_date);
      return now >= startDate && now <= endDate;
    }

    return false;
  };

  useEffect(() => {
    const emailDisabled = !emailFlag;
    const phoneDisabled = !phoneNumberFlag;

    const shouldDisableNames = emailDisabled && phoneDisabled;

    if (shouldDisableNames) {
      setFirstNameFlag(false);
      setLastNameFlag(false);
    }
  }, [emailFlag, phoneNumberFlag]);

  // Updated add period handler

  const [isPeriodOperationLoading, setIsPeriodOperationLoading] = useState(false);
  const handleAddPeriod = (copySettings) => {
    if (!planId) return;
    setIsPeriodOperationLoading(true);
    const payload = copySettings && selectedPeriod ? { copy_from_period_id: selectedPeriod.period_id } : {};
    createGuestPortalPeriod.mutate(
      { planId, formData: payload },
      {
        onSuccess: async () => {
          setToastMessage("Schedule added");
          await refetchAllPeriodSettings();
          setIsPeriodOperationLoading(false);
        },
        onError: (error) => {
          setToastMessage(error?.response?.data?.detail || "Failed to add schedule");
          setIsPeriodOperationLoading(false);
        },
      }
    );
  };

  const handleDeletePeriod = (periodId, periodName) => {
    if (!planId || !periodId || !periodName) {
      return;
    }
    setIsPeriodOperationLoading(true);
    deleteGuestPortalPeriodByName.mutate(
      { planId, periodName },
      {
        onSuccess: async () => {
          setToastMessage("Schedule deleted");
          await refetchAllPeriodSettings();
          setIsPeriodOperationLoading(false);
        },
        onError: (error) => {
          setToastMessage(error?.response?.data?.error || "Failed to delete schedule");
          setIsPeriodOperationLoading(false);
        },
      }
    );
  };


  // Enhanced boundary editing
  const handleEditPeriodBoundary = (leftPeriodId, newLeftEnd, rightPeriodId, newRightStart) => {
    // Update periods state locally
    const updatedPeriods = periods.map((period) => {
      if (period.id === leftPeriodId) {
        trackPeriodChange(leftPeriodId, "end_time", newLeftEnd);
        return { ...period, end_time: newLeftEnd };
      }
      if (period.id === rightPeriodId) {
        trackPeriodChange(rightPeriodId, "start_time", newRightStart);
        return { ...period, start_time: newRightStart };
      }
      return period;
    });
    setPeriods(updatedPeriods);


    // Immediately call the new API to update period times
    // Build the minimal periodStructure for the API
    const periodStructure = {};
    if (updatedPeriods.length === 1) {
      periodStructure[updatedPeriods[0].id] = {};
    } else {
      updatedPeriods.forEach((period) => {
        periodStructure[period.id] = {
          start_time: period.start_time,
          end_time: period.end_time,
        };
      });
    }
    updateGuestPortalPlanPeriods.mutate({ planId, periodStructure });
  };

  // --- Update handleBannerImageUpload and handleBannerImageClear to always use selectedPeriod.period as key ---
  const handleBannerImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!validateFileSize(file)) {
      setToastMessage("Maximum file size of 1MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setBannerImagePreview(reader.result);
      setBannerImageFileName(file.name);
      // Track in periodChanges
      trackPeriodChange(selectedPeriod.period, "banner_image_upload", file);
      trackPeriodChange(selectedPeriod.period, "banner_image_preview", reader.result);
      trackPeriodChange(selectedPeriod.period, "banner_image_file_name", file.name);
      trackPeriodChange(selectedPeriod.period, "delete_banner_image", false);
      // Also update banner_data for this period to include the new image preview/filename
      const prevBanner = periodChanges[selectedPeriod.period]?.banner_data || {};
      trackPeriodChange(selectedPeriod.period, "banner_data", {
        ...prevBanner,
        image: reader.result,
        image_filename: file.name,
      });
    };
    reader.readAsDataURL(file);
    setToastMessage("Uploaded");
    if (bannerInputRef.current) {
      bannerInputRef.current.value = "";
    }
  };

  const handleBannerImageClear = () => {
    setBannerImagePreview(null);
    setBannerImageFileName("");
    if (bannerInputRef.current) {
      bannerInputRef.current.value = "";
    }
    // Track in periodChanges
    trackPeriodChange(selectedPeriod.period, "banner_image_upload", null);
    trackPeriodChange(selectedPeriod.period, "banner_image_preview", null);
    trackPeriodChange(selectedPeriod.period, "banner_image_file_name", "");
    trackPeriodChange(selectedPeriod.period, "delete_banner_image", true);
    // Also update banner_data for this period to clear image fields
    const prevBanner = periodChanges[selectedPeriod.period]?.banner_data || {};
    trackPeriodChange(selectedPeriod.period, "banner_data", {
      ...prevBanner,
      image: null,
      image_filename: "",
    });
  };



  // Add handlers to persist links and banner data in periodChanges
  const handleLinksChange = (newLinks) => {
    setLinks(newLinks);
    trackPeriodChange(selectedPeriod.period, "links_data", newLinks);
  };

  // --- Update handleBannerChange to always use selectedPeriod.period as key ---
  const handleBannerChange = (newBanner) => {
    setBannerTitle(newBanner.title || "");
    setBannerDescription(newBanner.description || "");
    // Store the raw value in state for UI
    setBannerLinkURL(newBanner.banner_url || "");
    setBannerBackgroundColor(newBanner.color || "#000000");
    setBannerTextColor(newBanner.text_color || "#FFFFFF");
    // Track banner data for the selected period only, and preserve image preview/filename if present
    trackPeriodChange(selectedPeriod.period, "banner_data", {
      ...newBanner,
      // Do NOT format banner_url here; keep it raw for UI and state
      image: periodChanges[selectedPeriod.period]?.banner_image_preview ?? null,
      image_filename: periodChanges[selectedPeriod.period]?.banner_image_file_name ?? "",
    });

  };

  useEffect(() => {
    // If privacy tab is selected but email is disabled, switch to branding tab
    if (activeTab === "policies" && !emailFlag) {
      setActiveTab("branding");
    }
  }, [emailFlag, activeTab, selectedPeriod]);

  return (
    <>
      {isPageLoading && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}

      {isPeriodOperationLoading && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}
      
      {!isPageLoading && (<Flex direction="column" gap="5" p="5" style={{ alignSelf: "stretch", width: "100%", height: "100%" }}>
          
        {/* Navigation header with back button */}
        <Flex style={{ justifyContent: "space-between", alignItems: "center", alignSelf: "stretch" }}>
          <Flex gap="4" style={{ alignItems: "center", flex: "1 0 0" }}>
            <IconButton
              size="2"
              variant="soft"
              color="gray"
              onClick={() => {
                isBrand
                  ? navigate(`/portal-brand/${brandId}/planner`, {
                      state: {
                        isBrand: true,
                        brandId: brandId,
                        brandName: brandName,
                        brandLocationCount: brandLocationCount,
                      },
                    })
                  : navigate(`/portal-location/${paramId}/planner`, {
                      state: {
                        isBrand: false,
                        locationName: locationName,
                        locationAddress: locationAddress,
                      },
                    });
              }}
            >
              <ArrowLeftIcon width={16} height={16} />
            </IconButton>
            <Flex gap="1" direction="column" style={{ flex: "1 0 0", alignItems: "flex-start" }}>
              <Text size="2" weight="medium">
                {isBrand ? brandName : locationName}
              </Text>
              {isBrand && brandLocationCount > 0 ? (
                <HoverCard.Root>
                  <HoverCard.Trigger>
                    <Text size="1" color="gray">
                      {brandLocationCount === 1 ? "1 location" : `${brandLocationCount} locations`}
                    </Text>
                  </HoverCard.Trigger>
                  <HoverCard.Content maxHeight="300px" style={{ padding: "var(--space-3)" }}>
                    <Flex direction="column" gap="3">
                      {locationsData
                        ?.filter((loc) => loc.brand_id === brandId)
                        .map((loc) => {
                          const live = liveAccessPointsStatus.find((l) => l.name === loc.name) || {};
                          const online = live.online_access_points_count ?? loc.online_access_points_count ?? 0;
                          const offline = live.offline_access_points_count ?? loc.offline_access_points_count ?? 0;
                          const statusColor = online > 0 && offline === 0 ? "var(--grass-9)" : online > 0 && offline > 0 ? "var(--amber-9)" : "var(--red-9)";
                          const isOnline = online > 0 && offline === 0;
                          const isPartial = online > 0 && offline > 0;
                          return (
                            <Flex key={loc.id} direction="column" gap="1">
                              <Flex align="center" gap="1">
                                <Tooltip content={isOnline ? "Online" : isPartial ? "Partially online" : "Offline"}>
                                  <Box style={{ background: statusColor, width: "8px", height: "8px", borderRadius: "4px" }} />
                                </Tooltip>
                                <Text size="1" weight="medium">
                                  {loc.name}
                                </Text>
                              </Flex>
                              <Text size="1" color="gray">
                                {loc.address || "No address"}
                              </Text>
                            </Flex>
                          );
                        })}
                    </Flex>
                  </HoverCard.Content>
                </HoverCard.Root>
              ) : (
                <Text size="1" color="gray">
                  {isBrand ? "0 locations" : locationAddress}
                </Text>
              )}
            </Flex>
          </Flex>
          {/* Plan Badge */}
          {plan && (
            <Flex direction="column" gap="2" style={{ alignItems: "center", flex: "1 0 0" }}>
              {(() => {
                const { showGreenDot, badgeColor, badgeLabel, infoText } = getPlanBadgeInfo({
                  plan,
                  plansData,
                  guestPortalSettings: allPeriodSettings, // Using allPeriodSettings instead of currentPeriodSettings
                  formatDate,
                  formatDateMinusOne,
                });
                return (
                  <Flex direction="column" gap="2" style={{ alignItems: "center", flex: "1 1 0", alignSelf: "stretch" }}>
                    <Flex gap="2" align="center">
                      {showGreenDot && <Box style={{ background: "var(--grass-9)", width: "8px", height: "8px", borderRadius: "4px" }} />}
                      <Badge color={badgeColor}>{badgeLabel}</Badge>
                    </Flex>
                    <Text size="1" color="gray" style={{ textAlign: "center" }}>
                      {infoText}
                    </Text>
                  </Flex>
                );
              })()}
            </Flex>
          )}
          <Flex gap="2" style={{ justifyContent: "flex-end", flex: "1 0 0", alignItems: "center" }}>
            <Tooltip content={`Switch to ${theme === "light" ? "dark" : "light"} mode`} side="left">
              <IconButton size="2" variant="soft" color="gray" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
                {theme === "light" ? <MoonIcon width={16} height={16} /> : <SunIcon width={16} height={16} />}
              </IconButton>
            </Tooltip>
            <Tooltip content={`Switch to ${view === "mobile" ? "desktop" : "mobile"} view`} side="left">
              <IconButton size="2" variant="soft" color="gray" onClick={() => setView(view === "mobile" ? "desktop" : "mobile")}>
                {view === "mobile" ? <ComputerDesktopIcon width={16} height={16} /> : <DevicePhoneMobileIcon width={16} height={16} />}
              </IconButton>
            </Tooltip>
            {plan && !plan.is_main && (
              <EditPlanPopover
                isOpen={isEditPlanPopoverOpen}
                onOpenChange={setIsEditPlanPopoverOpen}
                plan={plan}
                plansData={plansData}
                isBrand={isBrand}
                brandId={brandId}
                brandLocationCount={brandLocationCount}
                effectiveLocationId={effectiveLocationId}
                onSuccess={(message, isDeleted) => {
                  setToastMessage(message);
                  if (isDeleted) {
                    // Navigate back to planner after deletion
                    if (isBrand) {
                      navigate(`/portal-brand/${brandId}/planner`, {
                        state: {
                          isBrand: true,
                          brandId: brandId,
                          brandName: brandName,
                          brandLocationCount: brandLocationCount,
                        },
                      });
                    } else {
                      navigate(`/portal-location/${paramId}/planner`, {
                        state: {
                          isBrand: false,
                          locationName: locationName,
                          locationAddress: locationAddress,
                        },
                      });
                    }
                  }
                }}
                onError={(message) => setToastMessage(message)}
                isDeleteAlertOpen={isDeleteAlertOpen}
                setIsDeleteAlertOpen={setIsDeleteAlertOpen}
                isActivePlan={isCurrentPlanActive()}
              >
                <IconButton size="2" variant="soft" color="gray">
                  <DotsHorizontalIcon width={16} height={16} />
                </IconButton>
              </EditPlanPopover>
            )}
          </Flex>
        </Flex>
        <React.Fragment>
          <Flex gap="5" style={{ alignSelf: "stretch", width: "100%", height: "100%", alignItems: "center", flex: "1 0 0" }}>
            {/* Settings Section */}
            <Flex style={{ alignItems: "flex-start", alignSelf: "stretch", overflow: "hidden" }} height="100%">
              <Form.Root onSubmit={handleSubmit} style={{ height: "100%", width: "100%" }}>
                <Flex
                  justify="between"
                  maxWidth="400px"
                  direction="column"
                  style={{
                    alignItems: "flex-start",
                    alignSelf: "stretch",
                    flex: "1 0 0",
                    borderRadius: "var(--radius-4)",
                    height: "100%",
                    border: "1px solid var(--gray-a6)",
                    backgroundColor: "var(--color-panel-solid)",
                    overflow: "hidden",
                  }}>
                  <Flex direction="column" style={{ alignSelf: "stretch", overflow: "scroll", flex: "1 0 0" }}>
                    {/* Settings Header */}
                    <Tabs.Root
                      defaultValue="portal"
                      value={activeTab}
                      onValueChange={(value) => {
                        setActiveTab(value);
                        // Active tab is persisted via state
                      }}>
                      <Flex direction="column" style={{ position: "sticky", top: "0", zIndex: "2", 
                        backgroundColor: "var(--color-panel-solid)", overflow: "hidden" }}>

                        {periods.length > 0 && (
                          <Flex direction="column" style={{ alignSelf: "stretch" }}>
                            <Flex p="4" gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                              <PeriodSelector
                                periods={periods}
                                selectedPeriod={selectedPeriod}
                                onPeriodClick={handlePeriodClick}
                                onAddPeriod={handleAddPeriod}
                                onDeletePeriod={(id, period) => handleDeletePeriod(id, period)}
                                onEditPeriodBoundary={handleEditPeriodBoundary}
                                resetUnsavedChanges={resetUnsavedPeriodChanges}
                                isPeriodLoading={isPeriodOperationLoading}
                              />
                              { periods.length > 1 && (
                                <Text size="1" weight="regular" color="gray">
                                  Drag the period boundaries to adjust the schedule.
                                </Text>
                              )}
                            </Flex>
                            <Separator size="4" orientation="horizontal" />
                          </Flex>
                        )}
                        <Tabs.List
                          style={{
                            padding: "var(--space-2) var(--space-4) 0px var(--space-4)",
                            borderTopLeftRadius: "var(--radius-4)",
                            borderTopRightRadius: "var(--radius-4)",
                          }}
                        >
                          <Tabs.Trigger value="branding">Branding</Tabs.Trigger>
                          <Tabs.Trigger value="portal">Form</Tabs.Trigger>
                          {emailFlag && <Tabs.Trigger value="policies">Privacy</Tabs.Trigger>}
                          <Tabs.Trigger value="landing">Landing</Tabs.Trigger>
                        </Tabs.List>
                        
                      </Flex>

                      {/* Portal Tab */}
                      <Tabs.Content value="portal" key="portal-tab">
                        <Flex p="4" gap="2" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="1" weight="regular" color="gray">
                            Your portal will appear on your guests' device when they connect to your guest Wi-Fi for the first time.
                          </Text>
                        </Flex>
                        {/* Email, Age, Gender, First Name, and Last Name Selects */}
                        <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                        <Flex p="4" gap="2" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Flex
                            style={{
                              justifyContent: "space-between",
                              alignSelf: "stretch",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <Text size="3" weight="medium">
                              Data fields
                            </Text>
                            <Text size="1" weight="regular" color="gray">
                              Maximum 4 fields
                            </Text>
                          </Flex>
                          <CheckboxGroup.Root
                            style={{ width: "100%", gap: "var(--space-2)" }}
                            value={[
                              emailFlag ? "email" : "",
                              ageFlag ? "age" : "",
                              genderFlag ? "gender" : "",
                              socialLoginFlag ? "social_login" : "",
                              firstNameFlag ? "first-name" : "",
                              lastNameFlag ? "last-name" : "",
                              phoneNumberFlag ? "phone-number" : "",
                              isDotdigitalActive() && organisationFlag ? "organisation" : "",
                            ]}
                            onValueChange={handleCpFormFieldsChange}
                          >
                            {/* First Name */}
                            <Flex
                              p="2"
                              style={{ justifyContent: "flex-start", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                            >
                              {shouldDisableNames ? (
                                <Tooltip content="You must enable email address or phone number first.">
                                  <Box>
                                    <CheckboxGroup.Item value="first-name" disabled>
                                      First Name
                                    </CheckboxGroup.Item>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <CheckboxGroup.Item value="first-name" disabled={isDisabled(firstNameFlag, true)}>
                                  First name
                                </CheckboxGroup.Item>
                              )}
                            </Flex>
                            {/* Last Name */}
                            <Flex
                              p="2"
                              style={{ justifyContent: "flex-start", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                            >
                              {shouldDisableNames ? (
                                <Tooltip content="You must enable email address or phone number first.">
                                  <Box>
                                    <CheckboxGroup.Item value="last-name" disabled>
                                      Last Name
                                    </CheckboxGroup.Item>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <CheckboxGroup.Item value="last-name" disabled={isDisabled(lastNameFlag, true)}>
                                  Last name
                                </CheckboxGroup.Item>
                              )}
                            </Flex>
                            <Flex
                              p="2"
                              style={{
                                justifyContent: "space-between",
                                alignSelf: "stretch",
                                alignItems: "center",
                                width: "100%",
                                borderRadius: "var(--radius-2)",
                                border: "1px solid var(--gray-a6)",
                              }}
                            >
                              <CheckboxGroup.Item value="email" disabled={isDisabled(emailFlag)}>
                                Email address
                              </CheckboxGroup.Item>
                              <Tooltip width="210px" content="Make sure to select your privacy notice method in the privacy tab." style={{ textAlign: "center" }}>
                                <ExclamationTriangleIcon color="gray" />
                              </Tooltip>
                            </Flex>
                            {/* Age */}
                            <Flex
                              p="2"
                              style={{
                                justifyContent: "space-between",
                                alignSelf: "stretch",
                                alignItems: "center",
                                width: "100%",
                                borderRadius: "var(--radius-2)",
                                border: "1px solid var(--gray-a6)",
                              }}
                            >
                              <CheckboxGroup.Item value="age" disabled={isDisabled(ageFlag)}>
                                Age
                              </CheckboxGroup.Item>
                              <Text as="label" size="1" color="gray">
                                <Flex gap="2">
                                  {isIntegrationActive() ? (
                                      <Tooltip 
                                        width="210px" 
                                        content="The Airship, Meta, Atreemo, and Como integrations are compatible only with Date of birth. If you are using one of these integrations, the Date of birth checkbox will be locked, but you can still modify the Age checkbox." 
                                        style={{ textAlign: "center" }}
                                      >
                                        <Flex style={{ display: "inline-flex" }}>
                                          <Checkbox 
                                            name="dob" 
                                            value="dob" 
                                            disabled={!ageFlag || isIntegrationActive()} 
                                            checked={ageFlag && dobFlag}
                                          />
                                        </Flex>
                                      </Tooltip>
                                    ) : (
                                    <Flex style={{ display: "inline-flex" }}>
                                      <Checkbox name="dob" value="dob" disabled={!ageFlag} checked={dobFlag && ageFlag} onCheckedChange={() => {
                                        const newDobFlag = !dobFlag;
                                        setDobFlag(newDobFlag);
                                        trackPeriodChange(selectedPeriod.id, "dob_flag", newDobFlag);
                                      }} />
                                    </Flex>
                                  )}
                                  Date of birth
                                </Flex>
                              </Text>
                            </Flex>
                            {/* Gender */}
                            <Flex
                              p="2"
                              style={{ justifyContent: "flex-start", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                            >
                              <CheckboxGroup.Item value="gender" disabled={isDisabled(genderFlag)}>
                                Gender
                              </CheckboxGroup.Item>
                            </Flex>
                            {/* Phone Number */}
                            <Flex
                              p="2"
                              style={{ justifyContent: "flex-start", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                            >
                              <CheckboxGroup.Item value="phone-number" disabled={isDisabled(phoneNumberFlag)}>
                                Phone number
                              </CheckboxGroup.Item>
                            </Flex>
                            {isDotdigitalActive() ? (
                              <Flex
                                p="2"
                                style={{ justifyContent: "space-between", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                              >
                                <CheckboxGroup.Item value="organisation" disabled={isDisabled(organisationFlag)}>
                                  Organisation
                                </CheckboxGroup.Item>
                                <Text as="label" size="1" color="gray">
                                  <Flex gap="2" align="center">
                                    {organisationRequiredFlag ? "Required" : "Optional"}
                                    <Switch
                                      size="1"
                                      checked={organisationRequiredFlag}
                                      disabled={!organisationFlag}
                                      onCheckedChange={(checked) => {
                                        setOrganisationRequiredFlag(checked);
                                        trackPeriodChange(selectedPeriod.id, "organisation_required_flag", checked);
                                      }}
                                    />
                                  </Flex>
                                </Text>
                              </Flex>
                            ) : null}
                          </CheckboxGroup.Root>
                        </Flex>
                        {/* Alternative Login Method Field */}
                        {userProfile?.add_social_login_button_state ? (
                          <React.Fragment>
                            <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                            <Flex style={{ alignSelf: "stretch" }} gap="2" p="4" direction="column" align="start">
                              <Text size="3" weight="medium">
                                Alternative log in methods
                              </Text>
                              <Flex style={{ alignSelf: "stretch" }} gap="1" direction="column" align="start">
                              <CheckboxGroup.Root
                                value={[
                                  emailFlag ? "email" : "",
                                  ageFlag ? "age" : "",
                                  genderFlag ? "gender" : "",
                                  firstNameFlag ? "first-name" : "",
                                  lastNameFlag ? "last-name" : "",
                                  phoneNumberFlag ? "phone-number" : "",
                                  isDotdigitalActive() && organisationFlag ? "organisation" : "",
                                  socialLoginFlag ? "social_login" : "",
                                ]}
                                onValueChange={handleCpFormFieldsChange}
                                style={{ width: "100%", gap: "var(--space-2)" }}
                              >
                                <Flex p="2" align="center" justify="start" style={{ alignSelf: "stretch", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}>
                                  <CheckboxGroup.Item value="social_login">Social login options</CheckboxGroup.Item>
                                </Flex>
                              </CheckboxGroup.Root>
                              <Text size="1" color="gray" weight="regular">Google login on guest portal is not supported on Apple devices, as restricted by Google.</Text>
                              </Flex>
                            </Flex>
                          </React.Fragment>
                        ) : null}
                        {/* Custom Message */}
                        <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                        <Flex p="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Message</Text>
                          <TextField.Root 
                            size="2"
                            value={message}
                            onChange={(e) => handleInputMessageChange(e.target.value)}
                            maxLength={48}
                            style={{ alignSelf: "stretch" }} />
                          <Text size="1" color="gray" weight="regular">
                            Maximum length of 48 characters.
                          </Text>
                        </Flex>
                      </Tabs.Content>
                      
                      {/* Privacy Policies Tab */}
                      <Tabs.Content value="policies" key="policies-tab">
                        <Flex direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Flex p="4" direction="column" gap="2" style={{ alignSelf: "stretch", flex: "1 0 0" }}>
                            <RadioGroup.Root
                              value={privacyType}
                              onValueChange={(value) => {
                                setPrivacyType(value);
                                trackPeriodChange(selectedPeriod.id, "privacy_type", value);
                              }}
                              style={{ width: "100%" }}
                            >
                              <Flex direction="row" gap="2" style={{ width: "100%" }}>
                                <Text as="label" size="2" style={{ width: "100%" }}>
                                  <Flex p="2" gap="2" style={{ alignItems: "center", flex: "1 0 0", border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                                    <RadioGroup.Item value="auto" />
                                    Auto
                                  </Flex>
                                </Text>
                                <Text as="label" size="2" style={{ width: "100%" }}>
                                  <Flex p="2" gap="2" style={{ alignItems: "center", flex: "1 0 0", border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                                    <RadioGroup.Item value="custom" />
                                    Custom
                                  </Flex>
                                </Text>
                              </Flex>
                            </RadioGroup.Root>

                            {privacyType === "auto" ? (
                              <Flex direction="column" gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                                <Text size="1" weight="regular" color="gray">
                                  Your privacy notice has been created using your legal information and will be displayed on your captive portal.
                                </Text>
                                <Button size="2" variant="soft" color="gray" style={{ alignSelf: "stretch" }} onClick={() => navigate("/organisation/details")}>
                                  Edit legal details
                                  <ArrowTopRightOnSquareIcon width="16" height="16" />
                                </Button>
                              </Flex>
                            ) : (
                              <Flex direction="column" gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                                <Text size="1" color="gray">
                                  Provide a custom privacy notice below. You can style it using&nbsp;
                                  <Link color="gray" underline="always" href="https://www.markdownguide.org/cheat-sheet/">
                                    markdown
                                  </Link>
                                  .
                                </Text>
                                <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                                  <Text size="2" weight="medium" style={{ width: "100%" }}>
                                    Privacy Profile
                                  </Text>
                                  <TextArea
                                    resize="vertical"
                                    size="2"
                                    style={{ width: "100%" }}
                                    rows="13"
                                    value={privacyPolicy}
                                    onChange={(e) => handlePrivacyPolicyChange(e.target.value)}
                                    placeholder="Paste here"
                                  />
                                  <Text size="1" color="gray">
                                    Auto will be used as a fallback if no privacy notice is provided.
                                  </Text>
                                </Flex>
                              </Flex>
                            )}

                            <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                              <Flex gap="1" direction="column">
                                <Text size="2" weight="medium">
                                  Accept button text
                                </Text>
                                <TextField.Root
                                  size="2"
                                  value={privacyAcceptButtonText}
                                  placeholder="Accept & Connect"
                                  maxLength={25}
                                  onChange={(e) => {
                                    setPrivacyAcceptButtonText(e.target.value);
                                    trackPeriodChange(selectedPeriod.id, "privacy_accept_button_text", e.target.value);
                                  }}
                                />
                                <Text size="1" weight="regular" color="gray">
                                  Maximum length of 25 characters.
                                </Text>
                              </Flex>
                            </Text>

                            <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                              <Flex gap="1" direction="column">
                                <Text size="2" weight="medium">
                                  Connect button text
                                </Text>
                                <TextField.Root
                                  size="2"
                                  value={privacyDeclineButtonText}
                                  placeholder="Connect"
                                  maxLength={25}
                                  onChange={(e) => {
                                    setPrivacyDeclineButtonText(e.target.value);
                                    trackPeriodChange(selectedPeriod.id, "privacy_decline_button_text", e.target.value);
                                  }}
                                />
                                <Text size="1" weight="regular" color="gray">
                                  Maximum length of 25 characters.
                                </Text>
                              </Flex>
                            </Text>
                          </Flex>

                          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />

                          {/* Scratch Card section under Privacy Tab */}
                          <Flex p="4" direction="column" gap="4" style={{ alignSelf: "stretch", flex: "1 0 0" }}>
                            {userProfile?.scratch_card_feature_flag && (
                              <Flex gap="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                                {/* Header row */}
                                <Flex style={{ alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                                  {/* Left side: Checkbox + Title */}
                                  <Flex gap="2" style={{ alignItems: "center" }}>
                                    <Checkbox
                                      checked={scratchCardFlag}
                                      onCheckedChange={(e) => {
                                        setScratchCardFlag(e);
                                        trackPeriodChange(selectedPeriod.id, "scratch_card_flag", e);
                                      }}
                                    />
                                    <Text size="3" weight="medium">Scratch card rewards</Text>
                                  </Flex>

                                  {/* Right side: Button */}
                                  <Button
                                    type="button"
                                    size="1"
                                    variant="surface"
                                    color="gray"
                                    onClick={() => window.open("https://airship.co.uk", "_blank")}
                                  >
                                    Open airship.co.uk
                                  </Button>
                                </Flex>

                                {/* Scratch card config */}
                                {scratchCardFlag && (
                                  <Flex gap="4" direction="row" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                                    {/* Card background colour */}
                                    <Form.Field name="scratch_card_background_colour" style={{ flex: 1, minWidth: 0 }}>
                                      <Flex gap="1" direction="column">
                                        <Form.Label>
                                          <Text size="2" weight="medium">Card background colour</Text>
                                        </Form.Label>
                                        <Flex gap="2" style={{ alignItems: "center", width: "100%" }}>
                                          {/* Colour square */}
                                          <label htmlFor="card_bg_colour_picker" style={{ position: "relative", display: "inline-block" }}>
                                            <input
                                              type="color"
                                              id="card_bg_colour_picker"
                                              value={scratchCardBackgroundColor}
                                              onChange={(e) => {
                                                setScratchCardBackgroundColor(e.target.value);
                                                trackPeriodChange(selectedPeriod.id, "scratch_card_background_colour", e.target.value);
                                              }}
                                              style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                            />
                                            <div
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: scratchCardBackgroundColor,
                                                borderRadius: "var(--radius-2)",
                                                border: "1px solid var(--gray-a6)"
                                              }}
                                            ></div>
                                          </label>

                                          {/* Hex input */}
                                          <Form.Control asChild>
                                            <input
                                              type="text"
                                              value={scratchCardBackgroundColor}
                                              onChange={(e) => {
                                                setScratchCardBackgroundColor(e.target.value);
                                                trackPeriodChange(selectedPeriod.id, "scratch_card_background_colour", e.target.value);
                                              }}
                                              style={{
                                                flex: 1,
                                                minWidth: 0,
                                                height: "32px",
                                                border: "1px solid var(--gray-a6)",
                                                borderRadius: "var(--radius-2)",
                                                padding: "0 8px",
                                                fontFamily: "monospace"
                                              }}
                                            />
                                          </Form.Control>
                                        </Flex>
                                      </Flex>
                                    </Form.Field>

                                    {/* Scratch area colour */}
                                    <Form.Field name="scratch_card_area_colour" style={{ flex: 1, minWidth: 0 }}>
                                      <Flex gap="1" direction="column">
                                        <Form.Label>
                                          <Text size="2" weight="medium">Scratch area colour</Text>
                                        </Form.Label>
                                        <Flex gap="2" style={{ alignItems: "center", width: "100%" }}>
                                          {/* Colour square */}
                                          <label htmlFor="scratch_area_colour_picker" style={{ position: "relative", display: "inline-block" }}>
                                            <input
                                              type="color"
                                              id="scratch_area_colour_picker"
                                              value={scratchCardAreaColor}
                                              onChange={(e) => {
                                                setScratchCardAreaColor(e.target.value);
                                                trackPeriodChange(selectedPeriod.id, "scratch_card_area_colour", e.target.value);
                                              }}
                                              style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                            />
                                            <div
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: scratchCardAreaColor,
                                                borderRadius: "var(--radius-2)",
                                                border: "1px solid var(--gray-a6)"
                                              }}
                                            ></div>
                                          </label>

                                          {/* Hex input */}
                                          <Form.Control asChild>
                                            <input
                                              type="text"
                                              value={scratchCardAreaColor}
                                              onChange={(e) => {
                                                setScratchCardAreaColor(e.target.value);
                                                trackPeriodChange(selectedPeriod.id, "scratch_card_area_colour", e.target.value);
                                              }}
                                              style={{
                                                flex: 1,
                                                minWidth: 0,
                                                height: "32px",
                                                border: "1px solid var(--gray-a6)",
                                                borderRadius: "var(--radius-2)",
                                                padding: "0 8px",
                                                fontFamily: "monospace"
                                              }}
                                            />
                                          </Form.Control>
                                        </Flex>
                                      </Flex>
                                    </Form.Field>
                                  </Flex>
                                )}
                              </Flex>
                            )}
                          </Flex>
                          {userProfile?.scratch_card_feature_flag && privacyType === "auto" && (
                            <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                          )}
                        </Flex>
                      </Tabs.Content>
                      
                      {/* Branding Tab */}
                      <Tabs.Content value="branding" key="branding-tab">
                        <Flex direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          {/* Portal font */}
                          <Flex gap="1" p="4" direction="column" style={{ alignSelf: "stretch" }}>
                            <Text as="label" size="2" weight="medium" htmlFor="portal_font_family">
                              Font
                            </Text>
                            <Select.Root
                              value={fontFamily}
                              onValueChange={(value) => {
                                setFontFamily(value);
                                trackPeriodChange(selectedPeriod.id, "font_family", value);
                              }}
                            >
                              <Select.Trigger
                                id="portal_font_family"
                                style={{ width: "100%", fontFamily: getPortalFontCssFamily(fontFamily) }}
                              />
                              <Select.Content position="popper">
                                {PORTAL_FONTS.map((font) => (
                                  <Select.Item key={font.key} value={font.key}>
                                    <span style={{ fontFamily: font.cssFamily }}>{font.label}</span>
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Root>
                            <Text size="1" weight="regular" color="gray">
                              Applied across the guest form, policies, links, banners, and landing page.
                            </Text>
                          </Flex>

                          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />

                          {/* Button Colour & text */}
                          <Flex gap="4" p="4" direction="column" style={{ alignSelf: "stretch" }}>
                            {/* Button Colour Input Field */}
                            <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                              <Form.Field name="button_color">
                                <Form.Label style={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}>
                                  <Text size="2" weight="medium">
                                    Button colour
                                  </Text>
                                </Form.Label>
                                <Form.Control asChild>
                                  <input type="hidden" name="button_color" value={buttonColor} />
                                </Form.Control>
                                <Flex direction="column" style={{ alignSelf: "stretch" }}>
                                  <label htmlFor="button_colour_picker" style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                    <input
                                      type="color"
                                      id="button_colour_picker"
                                      value={buttonColor}
                                      onChange={(e) => {
                                        setButtonColor(e.target.value);
                                        trackPeriodChange(selectedPeriod.id, "button_colour", e.target.value);
                                      }}
                                      style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                    />
                                    <div style={{ width: "100%", height: "32px", backgroundColor: buttonColor, borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}></div>
                                  </label>
                                </Flex>
                              </Form.Field>
                              <Text size="1" weight="regular" color="gray">
                                Recommended contrast of 3:1, against white. Visit &nbsp;
                                <Link color="blue" underline="always" target="_blank" href="https://webaim.org/resources/contrastchecker/">
                                  WebAim's Contrast Checker
                                </Link>
                                &nbsp;to test your button colour.
                              </Text>
                            </Flex>
                            {/* Button text input field */}
                            <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                              <Flex gap="1" direction="column">
                                <Text size="2" weight="medium">
                                  Button text
                                </Text>
                                <TextField.Root
                                  size="2"
                                  value={buttonText}
                                  placeholder="Connect to the internet"
                                  maxLength={25}
                                  onChange={(e) => {
                                      setButtonText(e.target.value);
                                      trackPeriodChange(selectedPeriod.id, "button_text", e.target.value);
                                  }}
                                />
                                <Text size="1" weight="regular" color="gray">
                                  Maximum length of 25 characters.
                                </Text>
                              </Flex>
                            </Text>
                          </Flex>

                          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                          
                          {/* Logo Input Field */}
                          <Flex gap="1" p="4" direction="column" style={{ alignSelf: "stretch" }}>
                            <Form.Field name="logo_image">
                              <Form.Label style={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}>
                                <Text size="2" weight="medium">
                                  Logo
                                </Text>
                              </Form.Label>
                              <Flex gap="2">
                                <input type="file" style={{ display: "none" }} ref={logoInputRef} onChange={handleLogoUpload} accept="image/*" />
                                <Button
                                  type="button"
                                  size="2"
                                  variant="surface"
                                  color="gray"
                                  style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                                  onClick={triggerLogoInput}
                                >
                                  {logoPreview ? (
                                    <Tooltip content="Upload new to replace">
                                      <Text>{logoFileName?.length >= 18 ? `${logoFileName.slice(0, 18)}...` : logoFileName}</Text>
                                    </Tooltip>
                                  ) : (
                                    <>
                                      <UploadIcon />
                                      <Text>Upload</Text>
                                    </>
                                  )}
                                </Button>
                                {logoPreview && (
                                  <Button type="button" size="2" variant="surface" color="gray" onClick={() => handleLogoClear()}>
                                    Clear
                                  </Button>
                                )}
                              </Flex>
                            </Form.Field>
                            <Text size="1" weight="regular" color="gray">
                              Recommended dimensions: 200 x 96 px. Maximum file size: 1 MB.
                            </Text>
                            <LogoSizeControl
                              value={logoSize}
                              disabled={!logoPreview}
                              onChange={(size) => {
                                setLogoSize(size);
                                trackPeriodChange(selectedPeriod.id, "logo_size", size);
                              }}
                            />
                          </Flex>

                          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />

                          {/* Background Image & Background Overlay */}
                          <Flex gap="4" p="4" direction="column" style={{ alignSelf: "stretch" }}>
                            {/* Background Image */}
                            <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                              <Form.Field name="background_image">
                                <Form.Label style={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}>
                                  <Text size="2" weight="medium">
                                    Background image
                                  </Text>
                                </Form.Label>
                                <Flex gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                                  <input
                                    type="file"
                                    style={{ display: "none" }}
                                    ref={backgroundInputRef}
                                    onChange={handleBackgroundImageUpload}
                                    id="background-upload"
                                    accept="image/*"
                                    name="background_image"
                                  />
                                  <Button
                                    type="button"
                                    size="2"
                                    variant="surface"
                                    color="gray"
                                    style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                                    onClick={triggerBackgroundInput}
                                  >
                                    {backgroundPreview ? (
                                      <Tooltip content="Upload new to replace">
                                        <Text>{backgroundFileName?.length >= 18 ? `${backgroundFileName.slice(0, 18)}...` : backgroundFileName}</Text>
                                      </Tooltip>
                                    ) : (
                                      <>
                                        <UploadIcon />
                                        <Text>Upload</Text>
                                      </>
                                    )}
                                  </Button>
                                  {backgroundPreview && (
                                    <Button type="button" size="2" variant="surface" color="gray" onClick={() => handleBackgroundImageClear()}>
                                      Clear
                                    </Button>
                                  )}
                                </Flex>
                              </Form.Field>
                              <Text size="1" weight="regular" color="gray">
                                Recommended dimensions: 900 x 600 px. Maximum file size: 1 MB.
                              </Text>
                            </Flex>
                            
                            {/* Background Overlay */}
                            <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                              <Form.Field name="background_overlay">
                                <Form.Label style={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}>
                                  <Text size="2" weight="medium">
                                    Background overlay
                                  </Text>
                                </Form.Label>
                                <Form.Control asChild>
                                  <input type="hidden" name="background_overlay" value={backgroundOverlayColor} />
                                </Form.Control>
                                <Flex gap="2" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                                  <label htmlFor="background_overlay_colour_picker" style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                    <input
                                      type="color"
                                      id="background_overlay_colour_picker"
                                      value={backgroundOverlayColor}
                                      onChange={(e) => {
                                        setBackgroundOverlayColor(e.target.value);
                                        trackPeriodChange(selectedPeriod.id, "background_overlay_colour", e.target.value);
                                      }}
                                      style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                    />
                                    {/* Custom color display */}
                                    <div
                                      style={{
                                        width: "100%",
                                        height: "32px",
                                        display: "flex",
                                        backgroundColor: colorConversion(backgroundOverlayColor, backgroundOverlayOpacity),
                                        borderRadius: "var(--radius-2)",
                                        border: "1px solid var(--gray-a6)",
                                      }}
                                    ></div>
                                  </label>
                                  <TextField.Root
                                    size="2"
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={backgroundOverlayOpacity}
                                    onChange={(e) => {
                                      setBackgroundOverlayOpacity(e.target.value);
                                      trackPeriodChange(selectedPeriod.id, "background_overlay_opacity", e.target.value);
                                    }}
                                    style={{ textAlign: "center", width: "70px" }}
                                  >
                                    <TextField.Slot side="right" style={{ padding: "4px" }}>
                                      %
                                    </TextField.Slot>
                                  </TextField.Root>
                                </Flex>
                                <Text size="1" weight="regular" color="gray">
                                  Recommended for logo visibility.
                                </Text>
                              </Form.Field>
                            </Flex>
                          </Flex>
                                                  
                        </Flex>
                      </Tabs.Content>
                      
                      {/* Landing Tab */}
                      <Tabs.Content value="landing" key="landing-tab">
                        {/* Page / Redirect */}
                        <Flex p="4" direction="column" gap="2" style={{ alignItems: "center", alignSelf: "stretch", flex: "1 0 0" }}>
                          <RadioGroup.Root
                            value={segmentView}
                            onValueChange={(value) => {
                              setSegmentView(value);
                              setLandingType(value); // Updating landing type when switching tabs
                              trackPeriodChange(selectedPeriod.id, "landing_type", value);
                            }}
                            style={{ width: "100%" }}
                          >
                            <Flex direction="row" gap="2" style={{ width: "100%" }}>
                              <Text as="label" size="2" style={{ width: "100%" }}>
                                <Flex p="2" gap="2" style={{ alignItems: "center", flex: "1 0 0", border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                                  <RadioGroup.Item value="page" />
                                  Page
                                </Flex>
                              </Text>
                              <Text as="label" size="2" style={{ width: "100%" }}>
                                <Flex p="2" gap="2" style={{ alignItems: "center", flex: "1 0 0",
                                  border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                                    <RadioGroup.Item value="redirect" />
                                    Redirect
                                </Flex>
                              </Text>
                            </Flex>
                          </RadioGroup.Root>
                          <Flex gap="4" style={{ alignItems: "center", alignSelf: "stretch", justifyContent: "space-between" }}>
                            <Text size="1" weight="regular" color="gray">
                              {segmentView === "page" 
                                ? "Your landing page will appear after a guest has been authorised to connect to the internet."
                                : "When Redirect is enabled or modified, the Access Point(s) will soft restart, causing a brief interruption for guest connections."
                              }
                            </Text>
                            <Tooltip content="This applies mostly to iOS, MacOS & Windows devices. Most Android devices
                              auto-close the portal upon connection."
                            >
                              <InfoCircledIcon color="gray" width={24} height={24} />
                            </Tooltip>
                          </Flex>
                        </Flex>
                        <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                        {/* Page Tab */}
                        {segmentView === "page" ? (
                          <Flex direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                            {/* Landing branding */}
                            <Flex p="4" gap="4" direction="column" style={{ alignSelf: "stretch" }}>
                              {/* Checkbox row */}
                              <Text as="label" size="2" style={{ width: "100%" }}>
                                <Flex gap="2" style={{ alignItems: "center", alignSelf: "stretch" }}>
                                  <Checkbox
                                    checked={landingBrandingFlag}
                                    onCheckedChange={(e) => {
                                      setLandingBrandingFlag(e);
                                      trackPeriodChange(selectedPeriod.id, "landing_branding_flag", e);
                                    }}
                                  />
                                  <Text size="3" weight="medium">
                                    Custom landing page branding
                                  </Text>
                                </Flex>
                              </Text>

                              {landingBrandingFlag && (
                                <>
                                  {/* Landing Logo */}
                                  <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                                    <Form.Field name="landing_logo_image">
                                      <Form.Label style={{ marginBottom: "4px" }}>
                                        <Text size="2" weight="medium">Logo</Text>
                                      </Form.Label>

                                      <Flex gap="2" style={{ alignItems: "center" }}>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          style={{ display: "none" }}
                                          ref={landingLogoInputRef}
                                          onChange={handleLandingLogoUpload}
                                        />
                                        <Button
                                          type="button"
                                          size="2"
                                          variant="surface"
                                          color="gray"
                                          style={{ flex: "1", justifyContent: "center" }}
                                          onClick={triggerLandingLogoInput}
                                        >
                                          {landingLogoPreview ? (
                                            <Tooltip content="Upload new to replace">
                                              <Text>
                                                {landingLogoFileName?.length >= 18
                                                  ? `${landingLogoFileName.slice(0, 18)}...`
                                                  : landingLogoFileName}
                                              </Text>
                                            </Tooltip>
                                          ) : (
                                            <>
                                              <UploadIcon />
                                              <Text>Upload</Text>
                                            </>
                                          )}
                                        </Button>
                                        {landingLogoPreview && (
                                          <Button
                                            type="button"
                                            size="2"
                                            variant="surface"
                                            color="gray"
                                            onClick={() => handleLandingLogoClear()}
                                          >
                                            Clear
                                          </Button>
                                        )}
                                      </Flex>
                                    </Form.Field>
                                    <Text size="1" weight="regular" color="gray">
                                      Recommended dimensions: 200 x 96 px. Maximum file size: 1 MB.
                                    </Text>
                                    <LogoSizeControl
                                      label="Landing logo size"
                                      value={landingLogoSize}
                                      disabled={!landingLogoPreview}
                                      onChange={(size) => {
                                        setLandingLogoSize(size);
                                        trackPeriodChange(selectedPeriod.id, "landing_logo_size", size);
                                      }}
                                    />
                                  </Flex>

                                  {/* Landing Background Image */}
                                  <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                                    <Form.Field name="landing_background_image">
                                      <Form.Label style={{ marginBottom: "4px" }}>
                                        <Text size="2" weight="medium">Background image</Text>
                                      </Form.Label>

                                      <Flex gap="2" style={{ alignItems: "center" }}>
                                        <input
                                          type="file"
                                          accept="image/*"
                                          style={{ display: "none" }}
                                          ref={landingBackgroundInputRef}
                                          onChange={handleLandingBackgroundImageUpload}
                                        />
                                        <Button
                                          type="button"
                                          size="2"
                                          variant="surface"
                                          color="gray"
                                          style={{ flex: "1", justifyContent: "center" }}
                                          onClick={triggerLandingBackgroundInput}
                                        >
                                          {landingBackgroundPreview ? (
                                            <Tooltip content="Upload new to replace">
                                              <Text>
                                                {landingBackgroundFileName?.length >= 18
                                                  ? `${landingBackgroundFileName.slice(0, 18)}...`
                                                  : landingBackgroundFileName}
                                              </Text>
                                            </Tooltip>
                                          ) : (
                                            <>
                                              <UploadIcon />
                                              <Text>Upload</Text>
                                            </>
                                          )}
                                        </Button>
                                        {landingBackgroundPreview && (
                                          <Button
                                            type="button"
                                            size="2"
                                            variant="surface"
                                            color="gray"
                                            onClick={() => handleLandingBackgroundImageClear()}
                                          >
                                            Clear
                                          </Button>
                                        )}
                                      </Flex>
                                    </Form.Field>
                                    <Text size="1" weight="regular" color="gray">
                                      Recommended dimensions: 900 x 600 px. Maximum file size: 1 MB.
                                    </Text>
                                  </Flex>

                                  {/* Landing Background Overlay */}
                                  <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                                    <Form.Field name="landing_background_overlay">
                                      <Form.Label style={{ marginBottom: "4px" }}>
                                        <Text size="2" weight="medium">Background overlay</Text>
                                      </Form.Label>

                                      <Form.Control asChild>
                                        <input type="hidden" name="landing_background_overlay" value={landingBackgroundOverlayColor} />
                                      </Form.Control>

                                      <Flex gap="2" style={{ alignItems: "center" }}>
                                        <label style={{ flex: 1, position: "relative" }}>
                                          <input
                                            type="color"
                                            value={landingBackgroundOverlayColor}
                                            onChange={(e) => {
                                              setLandingBackgroundOverlayColor(e.target.value);
                                              trackPeriodChange(selectedPeriod.id, "landing_background_overlay_colour", e.target.value);
                                            }}
                                            style={{
                                              opacity: 0,
                                              position: "absolute",
                                              width: "100%",
                                              height: "100%",
                                              cursor: "pointer",
                                            }}
                                          />
                                          <div
                                            style={{
                                              width: "100%",
                                              height: "32px",
                                              borderRadius: "var(--radius-2)",
                                              border: "1px solid var(--gray-a6)",
                                              backgroundColor: colorConversion(landingBackgroundOverlayColor, landingBackgroundOverlayOpacity),
                                            }}
                                          />
                                        </label>

                                        <TextField.Root
                                          size="2"
                                          type="number"
                                          min={0}
                                          max={100}
                                          step={1}
                                          value={landingBackgroundOverlayOpacity}
                                          onChange={(e) => {
                                            setLandingBackgroundOverlayOpacity(e.target.value);
                                            trackPeriodChange(selectedPeriod.id, "landing_background_overlay_opacity", e.target.value);
                                          }}
                                          style={{ width: "70px", textAlign: "center" }}
                                        >
                                          <TextField.Slot side="right" style={{ padding: "4px" }}>
                                            %
                                          </TextField.Slot>
                                        </TextField.Root>
                                      </Flex>
                                    </Form.Field>
                                    <Text size="1" weight="regular" color="gray">
                                      Recommended for logo visibility.
                                    </Text>
                                  </Flex>
                                </>
                              )}
                            </Flex>
                            <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                            {/* Link Section under Page Tab */}
                            <Flex p="4" gap="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                              <Text as="label" size="2" style={{ width: "100%" }}>
                                <Flex gap="2" style={{ alignItems: "center", alignSelf: "stretch" }}>
                                  <Checkbox
                                    checked={showLinks}
                                    onCheckedChange={(e) => {
                                      setShowLinks(e);
                                      trackPeriodChange(selectedPeriod.period, "links_flag", e);
                                    }}
                                  />
                                  <Text size="3" weight="medium">
                                    Links
                                  </Text>
                                </Flex>
                              </Text>
                              {showLinks && (
                                <Flex direction="column" gap="2" style={{ width: "100%" }}>
                                  {/* Rendering existing links */}
                                  {links.map((link, index) => (
                                    <LinkEditor
                                      key={link.id || index}
                                      links={links}
                                      setLinks={setLinks}
                                      editingLink={link}
                                      editingIndex={index}
                                      canHaveUniqueUrls={canHaveUniqueUrls}
                                      brandLocations={brandLocations}
                                      setToastMessage={setToastMessage}
                                      setIsDirty={setIsDirty}
                                      onLinksChange={handleLinksChange} // Pass the handler
                                    >
                                      <Button type="button" size="2" variant="surface" color="gray" style={{ width: "100%", alignSelf: "stretch", justifyContent: "center" }}>
                                        {link.label || `Link ${index + 1}`}
                                      </Button>
                                    </LinkEditor>
                                  ))}

                                  {/* Add new link button */}
                                  {links.length < 3 && (
                                    <LinkEditor
                                      links={links}
                                      setLinks={setLinks}
                                      isAddingLink={true}
                                      canHaveUniqueUrls={canHaveUniqueUrls}
                                      brandLocations={brandLocations}
                                      setToastMessage={setToastMessage}
                                      setIsDirty={setIsDirty}
                                      onLinksChange={handleLinksChange} // Pass the handler
                                    >
                                      <Button size="2" variant="soft" color="gray" style={{ width: "100%", alignSelf: "stretch", gap: "var(--space-2)", justifyContent: "center" }}>
                                        <PlusIcon />
                                        Add link
                                      </Button>
                                    </LinkEditor>
                                  )}
                                </Flex>
                              )}
                            </Flex>
                            <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                            {/* Banner section under Page Tab */}
                            <Flex p="4" gap="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                              <Text as="label" size="2">
                                <Flex gap="2" style={{ alignItems: "center", alignSelf: "stretch" }}>
                                  <Checkbox
                                    checked={showBanner}
                                    onCheckedChange={(e) => {
                                      setShowBanner(e);
                                      trackPeriodChange(selectedPeriod.id, "banner_flag", e);
                                    }}
                                  />
                                  <Text size="3" weight="medium">
                                    Banner
                                  </Text>
                                </Flex>
                              </Text>
                              {showBanner && (
                                <Flex gap="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                                  {/* Banner title input field */}
                                  <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                    <Flex gap="1" direction="column">
                                      <Text size="2" weight="medium">
                                        Title
                                      </Text>
                                      <TextField.Root
                                        size="2"
                                        value={bannerTitle}
                                        onChange={(e) => {
                                          setBannerTitle(e.target.value);
                                          handleBannerChange({
                                            title: e.target.value,
                                            description: bannerDescription,
                                            banner_url: bannerLinkURL,
                                            color: bannerBackgroundColor,
                                            text_color: getButtonTextColorBasedOnBackground(bannerBackgroundColor),
                                          });
                                        }}
                                      />
                                    </Flex>
                                  </Text>
                                  {/* Banner description input field */}
                                  <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                    <Flex gap="1" direction="column">
                                      <Text size="2" weight="medium">
                                        Description
                                      </Text>
                                      <TextField.Root
                                        size="2"
                                        value={bannerDescription}
                                        onChange={(e) => {
                                          setBannerDescription(e.target.value);
                                          handleBannerChange({
                                            title: bannerTitle,
                                            description: e.target.value,
                                            banner_url: bannerLinkURL,
                                            color: bannerBackgroundColor,
                                            text_color: getButtonTextColorBasedOnBackground(bannerBackgroundColor),
                                          });
                                        }}
                                      />
                                      <Text size="1" weight="regular" color="gray">
                                        Recommended length of 100 characters.
                                      </Text>
                                    </Flex>
                                  </Text>
                                  {/* Banner URL field */}
                                  <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                    <Flex gap="1" direction="column">
                                      <Text size="2" weight="medium">
                                        Link URL
                                      </Text>
                                      <TextField.Root
                                        size="2"
                                        value={bannerLinkURL}
                                        onChange={(e) => {
                                          setBannerLinkURL(e.target.value);
                                          handleBannerChange({
                                            title: bannerTitle,
                                            description: bannerDescription,
                                            banner_url: e.target.value,
                                            color: bannerBackgroundColor,
                                            text_color: getButtonTextColorBasedOnBackground(bannerBackgroundColor),
                                          });
                                        }}
                                      />
                                    </Flex>
                                  </Text>
                                  {/* Banner Image upload field */}
                                  <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                    <Flex gap="1" direction="column">
                                      <Text size="2" weight="medium">
                                        Image
                                      </Text>
                                      <Flex gap="2">
                                        <input
                                          type="file"
                                          accept="image/*"
                                          id="banner-upload"
                                          name="banner_image"
                                          style={{ display: "none" }}
                                          ref={bannerInputRef}
                                          onChange={handleBannerImageUpload}
                                        />
                                        <Button
                                          type="button"
                                          size="2"
                                          variant="surface"
                                          color="gray"
                                          style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (bannerInputRef.current) bannerInputRef.current.click();
                                          }}
                                        >
                                          {bannerImagePreview ? (
                                            <Tooltip content="Upload new to replace">
                                              <Text>{bannerImageFileName?.length >= 18 ? `${bannerImageFileName.slice(0, 18)}...` : bannerImageFileName}</Text>
                                            </Tooltip>
                                          ) : (
                                            <>
                                              <UploadIcon />
                                              <Text>Upload</Text>
                                            </>
                                          )}
                                        </Button>
                                        {bannerImagePreview && (
                                          <Button
                                            type="button"
                                            size="2"
                                            variant="surface"
                                            color="gray"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleBannerImageClear();
                                            }}
                                          >
                                            Clear
                                          </Button>
                                        )}
                                      </Flex>
                                      <Text size="1" weight="regular" color="gray">
                                        Recommended dimensions: 96 x 96px. Maximum file size: 1MB.
                                      </Text>
                                    </Flex>
                                  </Text>
                                  {/* Banner background color picker */}
                                  <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                    <Flex gap="1" direction="column">
                                      <Text size="2" weight="medium">Background colour</Text>
                                      <Flex gap="2" style={{ alignItems: "center", width: "100%" }}>
                                        <label
                                          htmlFor="banner_bg_color_picker"
                                          style={{ position: "relative", display: "block", width: "100%", height: "32px" }}
                                        >
                                          <input
                                            type="color"
                                            id="banner_bg_color_picker"
                                            value={bannerBackgroundColor || "#000000"}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setBannerBackgroundColor(value);
                                              const text_color = getButtonTextColorBasedOnBackground(value);
                                              setBannerTextColor(text_color);
                                              handleBannerChange({
                                                title: bannerTitle,
                                                description: bannerDescription,
                                                banner_url: bannerLinkURL,
                                                color: value,
                                                text_color: text_color,
                                              });
                                            }}
                                            style={{
                                              opacity: 0,
                                              position: "absolute",
                                              inset: 0,
                                              border: "none",
                                              padding: 0,
                                              margin: 0,
                                              cursor: "pointer",
                                            }}
                                            aria-label="Banner background color picker"
                                          />
                                          <div
                                            style={{
                                              width: "100%",
                                              height: "32px",
                                              backgroundColor: bannerBackgroundColor || "#000000",
                                              borderRadius: "var(--radius-2)",
                                              border: "1px solid var(--gray-a6)",
                                            }}
                                          />
                                        </label>
                                      </Flex>
                                    </Flex>
                                  </Text>
                                </Flex>
                              )}
                            </Flex>
                            { !showBanner && (
                              <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                            )}
                          </Flex>
                        ) : (
                          // Redirect Tab
                          <Flex p="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                            <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                              {/* Redirect URL */}
                              <Text as="label" size="2">
                                <Flex>
                                  <Text size="2" weight="medium">
                                    Redirect URL
                                  </Text>
                                </Flex>
                              </Text>
                              <TextField.Root size="2" value={redirectUrl} onChange={(e) => handleInputRedirectUrlChange(e.target.value)} style={{ alignSelf: "stretch" }} />
                            </Flex>
                            <Text size="1" weight="regular" color="gray">
                              If a URL is not provided, your landing page will be used as a fallback.
                            </Text>
                          </Flex>
                        )}
                        {segmentView === "redirect" && <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />}
                      </Tabs.Content>
                    </Tabs.Root>
                  </Flex>

                  {/* Save Changes Button */}
                  <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                  <Flex p="4" direction="column" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                    <Button 
                      type="submit"
                      variant="solid"
                      style={{ alignSelf: "stretch", width: "100%" }}
                      disabled={!isDirty}
                      loading={isSaveChangesLoading}>
                      Save changes
                    </Button>
                  </Flex>
                </Flex>
              </Form.Root>
            </Flex>
            {/* Mockup Preview Section */}
            <Flex
              gap="5"
              direction="column"
              style={{
                alignItems: "center",
                alignSelf: "stretch",
                flex: "1 0 0",
                borderRadius: "var(--radius-4)",
                justifyContent: "center",
                overflow: "hidden",
                border: "1px solid var(--gray-a6)",
                backgroundColor: "var(--color-panel-solid)",
              }}
            >
              {/* Mobile / Desktop */}
              {view === "mobile" ? (
                <IPhoneMockup screenWidth={screenWidthMobile} frameColor="var(--gray-12)" statusbarColor="var(--gray-contrast)" transparentNavBar="true" hideStatusBar="true" screenType="notch">
                  <Suspense>
                    <CaptivePortalMockup
                      device={view}
                      scaleFactor={scaleFactorMobile}
                      logoPreview={logoPreview}
                      landingLogoPreview={landingLogoPreview}
                      backgroundPreview={backgroundPreview}
                      landingBackgroundPreview={landingBackgroundPreview}
                      backgroundOverlayColor={colorConversion(backgroundOverlayColor, backgroundOverlayOpacity)}
                      landingBackgroundOverlayColor={colorConversion(landingBackgroundOverlayColor, landingBackgroundOverlayOpacity)}
                      landingBrandingFlag={landingBrandingFlag}
                      buttonText={buttonText}
                      privacyAcceptButtonText={privacyAcceptButtonText}
                      privacyDeclineButtonText={privacyDeclineButtonText}
                      buttonColor={buttonColor}
                      buttonTextColor={buttonTextColor}
                      fontFamily={fontFamily}
                      logoSize={logoSize}
                      landingLogoSize={landingLogoSize}
                      email={emailFlag}
                      age={ageFlag}
                      dob={dobFlag}
                      gender={genderFlag}
                      firstName={firstNameFlag}
                      lastName={lastNameFlag}
                      phoneNumber={phoneNumberFlag}
                      organisation={isDotdigitalActive() && organisationFlag}
                      organisationRequired={organisationRequiredFlag}
                      scratchCardFlag={scratchCardFlag}
                      scratchCardBackgroundColor={scratchCardBackgroundColor}
                      scratchCardAreaColor={scratchCardAreaColor}
                      social_login={socialLoginFlag}
                      message={message}
                      activeTab={activeTab}
                      links_data={
                        showLinks
                          ? {
                              links: links,
                            }
                          : null
                      }
                      banner_data={
                        showBanner
                          ? {
                              title: bannerTitle,
                              description: bannerDescription,
                              banner_url: bannerLinkURL,
                              image: bannerImagePreview,
                              image_filename: bannerImageFileName,
                              color: bannerBackgroundColor,
                              text_color: bannerTextColor,
                            }
                          : null
                      }
                      segmentView={segmentView}
                      redirectUrl={redirectUrl}
                      orgDetails={orgDetails?.data}
                      darkMode={theme == "dark"}
                    />
                  </Suspense>
                </IPhoneMockup>
              ) : (
                // Desktop View
                <Suspense>
                  <Flex style={{ flex: "1 0 0", alignSelf: "stretch", justifyContent: "center" }}>
                    <CaptivePortalMockup
                      device={view}
                      scaleFactor={scaleFactorMobile}
                      logoPreview={logoPreview}
                      landingLogoPreview={landingLogoPreview}
                      backgroundPreview={backgroundPreview}
                      landingBackgroundPreview={landingBackgroundPreview}
                      backgroundOverlayColor={colorConversion(backgroundOverlayColor, backgroundOverlayOpacity)}
                      landingBackgroundOverlayColor={colorConversion(landingBackgroundOverlayColor, landingBackgroundOverlayOpacity)}
                      landingBrandingFlag={landingBrandingFlag}
                      buttonText={buttonText}
                      privacyAcceptButtonText={privacyAcceptButtonText}
                      privacyDeclineButtonText={privacyDeclineButtonText}
                      buttonColor={buttonColor}
                      buttonTextColor={buttonTextColor}
                      fontFamily={fontFamily}
                      logoSize={logoSize}
                      landingLogoSize={landingLogoSize}
                      email={emailFlag}
                      age={ageFlag}
                      dob={dobFlag}
                      gender={genderFlag}
                      firstName={firstNameFlag}
                      lastName={lastNameFlag}
                      phoneNumber={phoneNumberFlag}
                      organisation={isDotdigitalActive() && organisationFlag}
                      organisationRequired={organisationRequiredFlag}
                      scratchCardFlag={scratchCardFlag}
                      scratchCardBackgroundColor={scratchCardBackgroundColor}
                      scratchCardAreaColor={scratchCardAreaColor}
                      social_login={socialLoginFlag}
                      message={message}
                      activeTab={activeTab}
                      links_data={
                        showLinks
                          ? {
                              links: links,
                            }
                          : null
                      }
                      banner_data={
                        showBanner
                          ? {
                              title: bannerTitle,
                              description: bannerDescription,
                              banner_url: bannerLinkURL,
                              image: bannerImagePreview,
                              image_filename: bannerImageFileName,
                              color: bannerBackgroundColor,
                              text_color: bannerTextColor,
                            }
                          : null
                      }
                      segmentView={segmentView}
                      redirectUrl={redirectUrl}
                      orgDetails={orgDetails?.data}
                      darkMode={theme == "dark"}
                    />
                  </Flex>
                </Suspense>
              )}
            </Flex>
          </Flex>
        </React.Fragment>
      </Flex>)}
      <AlertDialog.Root open={showIntegrationsReminder} onOpenChange={setShowIntegrationsReminder}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Check your integrations</AlertDialog.Title>
          <AlertDialog.Description size="2">
            If you use integrations, check that their data field mappings are still up to date.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Stay here</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button onClick={() => navigate("/integrations/active")}>Go to integrations</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
      {/* Toast Updates */}
      <ToastAlert />
    </>
  );
};

export default GuestPortal;
